export type Rank = 'R5' | 'R4' | 'R3' | 'R2' | 'R1';
export type Profession = 'warlord' | 'engineer';
export type MType = 'planes' | 'rockets' | 'tanks';

export type AllianceFeature = 'transfers';

export interface AllianceBranding {
  id: number;
  name: string;
  tag: string | null;
  slug: string | null;
  motto: string | null;
  logoUrl: string | null;
}

export interface AuthUser {
  id: number;
  username: string;
  role: Rank;
  playerId: number | null;
  isSuperadmin: boolean;
  isServerAdmin: boolean;
  impersonator: { id: number; username: string } | null;
  alliance:
    | (AllianceBranding & {
        features: AllianceFeature[];
        server: { id: number; number: number; name: string | null } | null;
      })
    | null;
}

export interface AllianceSettings extends AllianceBranding {
  features: AllianceFeature[];
  lastrankUrl?: string | null;
}

export interface PlayerMoveTarget {
  id: number;
  name: string;
  tag: string | null;
  serverId: number | null;
  serverNumber: number | null;
}

export interface ServerRow {
  id: number;
  number: number;
  name: string | null;
  createdAt: string;
}

export interface TenantRow {
  id: number;
  name: string;
  tag: string | null;
  slug: string | null;
  active: boolean;
  serverId: number | null;
  serverNumber: number | null;
  userCount: number;
  playerCount: number;
  features: AllianceFeature[];
  createdAt: string;
}

export interface AdminUserRow {
  id: number;
  username: string;
  role: Rank;
  active: boolean;
  allianceId: number | null;
  allianceName: string | null;
  allianceTag: string | null;
  isSuperadmin: boolean;
  isServerAdmin: boolean;
  lastSeenAt: string | null;
}

export interface PowerSnapshot {
  id: number;
  playerId: number;
  totalPower: number;
  kills: number;
  m1Power: number;
  m1Type: MType;
  m2Power: number;
  m2Type: MType;
  m3Power: number;
  m3Type: MType;
  recordedAt: string;
  recordedBy: number | null;
  source?: string | null;
  strengthRequestId?: number | null;
}

export interface LeavePeriod {
  id: number;
  playerId: number;
  startsAt: string;
  endsAt: string | null;
  reason: string | null;
  createdAt: string;
  createdBy: number | null;
}

export const PLAYER_FLAG_KINDS = ['yellow', 'red', 'green', 'train_hint'] as const;
export type PlayerFlagKind = (typeof PLAYER_FLAG_KINDS)[number];

export interface PlayerFlag {
  id: number;
  playerId: number;
  kind: PlayerFlagKind;
  note: string | null;
  issuedAt: string;
  expiresAt: string | null;
  issuedBy?: number | null;
  revokedAt?: string | null;
  revokedBy?: number | null;
}

export type ActivePlayerFlag = Omit<PlayerFlag, 'revokedAt' | 'revokedBy'>;

export interface Player {
  id: number;
  name: string;
  displayName: string | null;
  realName: string | null;
  rank: Rank;
  profession: Profession | null;
  neverSub: boolean;
  active: boolean;
  deleted: boolean;
  publicId: number | null;
  country: string | null;
  baseLevel: number | null;
  careerType: number | null;
  preferredCsSlot: SlotKey | null;
  timezone: string | null;
  lastSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestSnapshot: PowerSnapshot | null;
  currentLeave: LeavePeriod | null;
  activeFlags: ActivePlayerFlag[];
}

export interface NameHistoryEntry {
  id: number;
  playerId: number;
  oldName: string;
  newName: string;
  source: string;
  changedAt: string;
}

export interface SnapshotBaseline {
  playerId: number;
  recordedAt: string;
  m1Power: number;
  m2Power: number;
  m3Power: number;
  kills: number;
}

export interface EventChangeLogEntry {
  id: number;
  eventId: number;
  playerId: number;
  fromSlot: SlotKey | null;
  fromRole: AssignedRole | null;
  toSlot: SlotKey | null;
  toRole: AssignedRole | null;
  source: string;
  changedAt: string;
  changedBy: number | null;
  changedByName: string | null;
  playerName: string;
  playerDisplayName: string | null;
}

export interface PlayerChangeLogEntry {
  id: number;
  playerId: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  comment: string | null;
  changedAt: string;
  changedBy: number | null;
  changedByName: string | null;
}

export interface SyncSummary {
  fetchedCount: number;
  matchedCount: number;
  unmatched: { publicId: number; name: string }[];
  nameChanges: { playerId: number; oldName: string; newName: string }[];
  rankChanges: { playerId: number; name: string; oldRank: Rank; newRank: Rank }[];
  snapshotsCreated: number;
  professionUpdates: { playerId: number; name: string; profession: string | null }[];
  errors: string[];
  durationMs: number;
}

export type SyncJobStatus = 'idle' | 'running' | 'success' | 'error';

export interface SyncJobState {
  id: number;
  status: SyncJobStatus;
  startedAt: string | null;
  finishedAt: string | null;
  total: number;
  processed: number;
  currentPlayer: string | null;
  message: string | null;
  result: SyncSummary | null;
}

export interface SyncStatus {
  current: SyncJobState | null;
  lastCompleted: SyncJobState | null;
}

export interface UserRow {
  id: number;
  username: string;
  role: Rank;
  playerId: number | null;
  active: boolean;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}

export type EventType = 'canyon_storm' | 'desert_storm' | 'special_ingame';
export type EventStatus = 'planned' | 'completed' | 'skipped';
export type SpecialAttendance = 'showed' | 'rulebreaker' | 'no_show';
export type SlotKey = 'A' | 'B';
export type AssignedRole = 'starter' | 'sub';

export interface EventSlot {
  id: number;
  eventId: number;
  slotKey: SlotKey;
  startsAtServer: string;
}

export interface EventCounts {
  appliedA: number;
  appliedB: number;
  startersA: number;
  startersB: number;
  subsA: number;
  subsB: number;
  pendingAttendance: number;
}

export interface EventSummary {
  id: number;
  type: EventType;
  eventDate: string;
  startsAt: string | null;
  status: EventStatus;
  customName: string | null;
  createdAt: string;
  createdBy: number | null;
  planningDoneAt: string | null;
  planningDoneBy: number | null;
  planningDoneByName?: string | null;
  slots: EventSlot[];
  counts: EventCounts;
}

export interface SpecialEventPlayer {
  id: number;
  name: string;
  displayName: string | null;
  rank: string;
  profession: string | null;
  active: boolean;
  m1Power: number | null;
  m1Type: string | null;
  totalPower: number | null;
}

export interface SpecialEventAssignment {
  id: number;
  position: number;
  playerId: number | null;
  locked: boolean;
  attendance: SpecialAttendance | null;
  notes: string | null;
  player: SpecialEventPlayer | null;
}

export interface SpecialEventTeam {
  id: number;
  teamIndex: number;
  name: string | null;
  capacity: number;
  strengthShare: number;
  strengthShareNormalized: number;
  leaderPlayerId: number | null;
  leader: SpecialEventPlayer | null;
  task: string | null;
  assignments: SpecialEventAssignment[];
  totals: {
    assigned: number;
    capacity: number;
    sumM1: number;
    sumTotal: number;
    targetTotal: number;
    deviation: number;
  };
}

export interface SpecialEventDetail {
  event: {
    id: number;
    type: 'special_ingame';
    eventDate: string;
    startsAt: string | null;
    status: EventStatus;
    customName: string | null;
    planningDoneAt: string | null;
    planningDoneBy: number | null;
    createdAt: string;
    createdBy: number | null;
  };
  teams: SpecialEventTeam[];
  unassigned: SpecialEventPlayer[];
}

export interface Participation {
  id: number;
  eventId: number;
  playerId: number;
  appliedA: boolean;
  appliedB: boolean;
  assignedSlot: SlotKey | null;
  assignedRole: AssignedRole | null;
  assignedPosition: number | null;
  flex: boolean;
  attendanceStatus: AttendanceStatus | null;
  notes: string | null;
  recordedAt: string;
  attendanceRecordedAt: string | null;
  swapContactMade: boolean;
  swapAim15Min: boolean;
  player: Player;
  metrics: PlayerMetrics | null;
}

export interface EventSwapMe {
  role: 'starter' | 'sub';
  position: number | null;
  contactMade: boolean;
  aim15Min: boolean;
}

export interface EventSwapCounterpart {
  playerId: number;
  name: string;
  timezone: string | null;
  role: 'starter' | 'sub';
  position: number | null;
  contactMade: boolean;
  aim15Min: boolean;
}

export interface EventSwapPayload {
  event: {
    id: number;
    type: 'canyon_storm' | 'desert_storm';
    eventDate: string;
    startsAtServer: string;
    slot: 'A' | 'B';
  };
  me: EventSwapMe;
  counterpart: EventSwapCounterpart | null;
}

export const ATTENDANCE_STATUSES = ['showed', 'late', 'no_show'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export interface RecentEventEntry {
  eventDate: string;
  type: EventType;
  slot: SlotKey | null;
  outcome: 'picked' | 'applied_not_picked' | 'not_applied';
  role: AssignedRole | null;
  attendance: AttendanceStatus | null;
  redCarded: boolean;
}

export interface PlayerMetrics {
  consecutiveNotPicked: number;
  consecutiveNotRegistered: number;
  consecutiveNoShows: number;
  consecutiveLates: number;
  consecutiveSubsOnly: number;
  lastApplied: string | null;
  lastPicked: string | null;
  recentEvents: RecentEventEntry[];
}

export interface Candidate {
  player: Player;
  latestSnapshot: PowerSnapshot | null;
  totalPower: number;
  metrics: PlayerMetrics;
  appliedThisSlot: boolean;
  appliedOtherSlot: boolean;
  assignedHere: AssignedRole | null;
  assignedOther: SlotKey | null;
}

export interface ParticipationHistoryItem extends Participation {
  event: Omit<EventSummary, 'slots' | 'counts'>;
}

export interface OcrSignupInput {
  name: string;
  role?: AssignedRole | null;
  slot?: SlotKey | null;
  frames_seen?: number;
}

export interface OcrImportMeta {
  source: string;
  video_file?: string;
  video_duration_seconds?: number;
  frame_count?: number;
  detected_slot_badge?: SlotKey | null;
  detected_fight_time?: { date: string; start: string; end: string } | null;
}

export interface OcrImportPayload {
  slot: SlotKey;
  signups: OcrSignupInput[];
  meta: OcrImportMeta;
}

export interface ImportMatchedRow {
  ocrName: string;
  role: AssignedRole | null;
  assignmentSlot: SlotKey | null;
  framesSeen: number;
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    active: boolean;
  };
  previousAppliedA: boolean;
  previousAppliedB: boolean;
  previousAssignedSlot: SlotKey | null;
  previousAssignedRole: AssignedRole | null;
  alreadyApplied: boolean;
  matchKind: 'exact_name' | 'exact_display' | 'fuzzy_unique';
  matchScore: number;
}

export interface ImportSuggestion {
  playerId: number;
  name: string;
  displayName: string | null;
  score: number;
}

export interface ImportUnmatchedRow {
  ocrName: string;
  role: AssignedRole | null;
  assignmentSlot: SlotKey | null;
  framesSeen: number;
  suggestions: ImportSuggestion[];
}

export type ImportConflict =
  | { kind: 'inactive_player_applied'; ocrName: string; playerId: number; playerName: string }
  | { kind: 'reimport_overwrite'; ocrName: string; playerId: number; playerName: string };

export interface ImportSlotInfo {
  inputSlot: SlotKey;
  detectedBadge: SlotKey | null;
  sharedTime: boolean;
  resolvedSlot: SlotKey | null;
  slotTimes: Record<SlotKey, string | null>;
}

export interface ImportPreviewResponse {
  event: { id: number; type: EventType; eventDate: string; status: EventStatus };
  slotInfo: ImportSlotInfo;
  meta: OcrImportMeta;
  fightTimeWarning: string | null;
  counts: { ocrRows: number; matched: number; unmatched: number; conflicts: number };
  matched: ImportMatchedRow[];
  unmatched: ImportUnmatchedRow[];
  conflicts: ImportConflict[];
}

export interface ImportConfirmRow {
  ocrName: string;
  playerId: number;
  role?: AssignedRole | null;
  assignmentSlot?: SlotKey | null;
}

export interface ImportConfirmResponse {
  ok: true;
  inserted: number;
  updated: number;
  assignmentsApplied: number;
  slot: SlotKey | null;
  sharedTime: boolean;
}

export type ImportJobStatus = 'queued' | 'running' | 'done' | 'failed';

export type ImportVideoJobStage = 'queued' | 'uploading' | 'processing' | 'done';

export interface ImportVideoJobState {
  id: string;
  status: ImportJobStatus;
  stage: ImportVideoJobStage | string;
  progress: { stage: ImportVideoJobStage; bytes: number };
  slot: SlotKey;
  videoBytes?: number;
  attempts?: number;
  nextRetryAt?: string | null;
  createdAt?: string;
  createdBy?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  appliedAt?: string | null;
  videoAvailable?: boolean;
  error: string | null;
  payload: OcrImportPayload | null;
}

export interface ImportVideoUploadResponse {
  jobId: string;
  bytes: number;
  slot: SlotKey;
}

export const EVENT_STATS_RANGES = ['30d', '90d', 'all'] as const;
export type EventStatsRange = (typeof EVENT_STATS_RANGES)[number];
export const EVENT_STATS_HISTORY_LIMITS = [5, 10, 20] as const;
export type EventStatsHistoryLimit = (typeof EVENT_STATS_HISTORY_LIMITS)[number];

export interface EventStatsRow {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    active: boolean;
    m1Power: number | null;
    m1Type: MType | null;
    totalPower: number | null;
  };
  counts: {
    events: number;
    registered: number;
    notRegistered: number;
    picked: number;
    notPicked: number;
    starter: number;
    sub: number;
    showed: number;
    late: number;
    noShow: number;
  };
  rates: {
    regRate: number | null;
    pickRate: number | null;
    noShowRate: number | null;
    subRate: number | null;
  };
  history: RecentEventEntry[];
}

export interface EventStatsResponse {
  events: { total: number; from: string | null; to: string | null };
  players: EventStatsRow[];
}

export interface SpecialEventStatsHistoryEntry {
  eventId: number;
  eventDate: string;
  customName: string | null;
  teamIndex: number | null;
  teamName: string | null;
  assigned: boolean;
  attendance: 'showed' | 'rulebreaker' | 'no_show' | null;
}

export interface SpecialEventStatsRow {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    active: boolean;
    m1Power: number | null;
    m1Type: MType | null;
    totalPower: number | null;
  };
  counts: {
    events: number;
    assigned: number;
    notAssigned: number;
    showed: number;
    rulebreaker: number;
    noShow: number;
    pending: number;
  };
  rates: {
    assignRate: number | null;
    showRate: number | null;
    noShowRate: number | null;
  };
  history: SpecialEventStatsHistoryEntry[];
}

export interface SpecialEventStatsResponse {
  events: { total: number; from: string | null; to: string | null };
  players: SpecialEventStatsRow[];
}

export interface DashboardStatPlayer {
  id: number;
  name: string;
  rank: Rank;
  neverSub: boolean;
  totalPower: number;
  metrics: PlayerMetrics;
}

export interface SquadLeader {
  id: number;
  name: string;
  rank: Rank;
  totalPower: number;
  m1Power: number;
}

export interface RecentRename {
  id: number;
  playerId: number;
  oldName: string;
  newName: string;
  source: string;
  changedAt: string;
}

export interface DashboardStats {
  shouldBePicked: DashboardStatPlayer[];
  recentNoShows: DashboardStatPlayer[];
  longRunningSubs: DashboardStatPlayer[];
  professionCounts: { warlord: number; engineer: number; none: number };
  strongestByM1Type: { planes: SquadLeader[]; rockets: SquadLeader[]; tanks: SquadLeader[] };
  recentRenames: RecentRename[];
}

export const EVENT_TYPE_LABEL: Record<EventType, string> = {
  canyon_storm: 'Canyon Storm',
  desert_storm: 'Desert Storm',
  special_ingame: 'Special event',
};
export const EVENT_TYPE_SHORT: Record<EventType, string> = {
  canyon_storm: 'CS',
  desert_storm: 'DS',
  special_ingame: 'SP',
};

export type SurveyStatus = 'open' | 'closed';
export type SurveyAnswer = 'yes' | 'no' | 'no_answer';
export const SURVEY_CATEGORIES = ['general', 'event_participation'] as const;
export type SurveyCategory = (typeof SURVEY_CATEGORIES)[number];
export const SURVEY_CATEGORY_LABEL: Record<SurveyCategory, string> = {
  general: 'General',
  event_participation: 'Event participation',
};

export interface SurveyCounts {
  yes: number;
  no: number;
  pending: number;
  total: number;
}

export interface Survey {
  id: number;
  title: string;
  description: string | null;
  category: SurveyCategory;
  status: SurveyStatus;
  createdAt: string;
  createdBy: number | null;
  closedAt: string | null;
  closedBy: number | null;
  counts: SurveyCounts;
}

export interface SurveyRow {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
  };
  answer: SurveyAnswer;
  notes: string | null;
  respondedAt: string | null;
  respondedBy: number | null;
  hasResponse: boolean;
}

export interface SurveyDetail {
  survey: Survey;
  rows: SurveyRow[];
}

export type StrengthRequestStatus = 'open' | 'closed';

export interface StrengthRequestCounts {
  submitted: number;
  pending: number;
  total: number;
  snapshotCount: number;
}

export interface StrengthRequest {
  id: number;
  title: string;
  status: StrengthRequestStatus;
  startedAt: string;
  createdBy: number | null;
  closedAt: string | null;
  closedBy: number | null;
  counts: StrengthRequestCounts;
}

export interface StrengthSnapshotSummary {
  id: number;
  totalPower: number;
  m1Power: number;
  m1Type: MType;
  m2Power: number;
  m2Type: MType;
  m3Power: number;
  m3Type: MType;
  recordedAt: string;
  recordedBy: number | null;
}

export interface StrengthRequestRow {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
  };
  submitted: boolean;
  snapshot: StrengthSnapshotSummary | null;
  latestSnapshot: StrengthSnapshotSummary | null;
}

export interface StrengthRequestDetail {
  request: StrengthRequest;
  rows: StrengthRequestRow[];
}

export interface StrengthStatsRow {
  playerId: number;
  name: string;
  displayName: string | null;
  fromM1: number;
  toM1: number;
  deltaM1: number;
  fromM2: number;
  toM2: number;
  deltaM2: number;
  fromM3: number;
  toM3: number;
  deltaM3: number;
  fromMSum: number;
  toMSum: number;
  deltaMSum: number;
  /** Set when a boundary value is estimated from reports a few days away. */
  fromEstimatedFrom: [string, string] | null;
  toEstimatedFrom: [string, string] | null;
}

export type StrengthMissingReason =
  | 'no_report_near_start'
  | 'no_report_near_end'
  | 'single_report';

export interface StrengthStatsMissingEntry {
  playerId: number;
  name: string;
  displayName: string | null;
  reason: StrengthMissingReason;
}

export interface StrengthStats {
  startDate: string;
  endDate: string;
  sampleSize: number;
  totalActivePlayers: number;
  averages: { m1: number; m2: number; m3: number; mSum: number };
  topM1: StrengthStatsRow[];
  flopM1: StrengthStatsRow[];
  topMSum: StrengthStatsRow[];
  flopMSum: StrengthStatsRow[];
  all: StrengthStatsRow[];
  missing: StrengthStatsMissingEntry[];
  maxGapDays: number;
}

export interface StrengthResponderRow {
  playerId: number;
  name: string;
  displayName: string | null;
  submittedAt: string;
  responseSeconds: number;
}

export interface StrengthResponders {
  requestId: number;
  startedAt: string;
  totalActivePlayers: number;
  submittedCount: number;
  fastest: StrengthResponderRow[];
  slowest: StrengthResponderRow[];
  all: StrengthResponderRow[];
}

export interface Neighbor {
  name: string;
  displayName: string | null;
  rank: Rank;
  value: number;
  gap: number;
}
export interface NeighborPair {
  prey: Neighbor | null;
  hunter: Neighbor | null;
}

export interface SelfReportRankNeighbor {
  name: string;
  displayName: string | null;
  value: number;
  /** Absolute distance to your own value. */
  gap: number;
}

export interface SelfReportRank {
  rank: number;
  total: number;
  above: SelfReportRankNeighbor | null;
  below: SelfReportRankNeighbor | null;
}

export interface SelfReportSummary {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    profession: Profession | null;
  };
  activeRequest: { id: number; title: string; startedAt: string } | null;
  latestApproved: {
    m1Power: number;
    m1Type: MType;
    m2Power: number;
    m2Type: MType;
    m3Power: number;
    m3Type: MType;
    kills: number;
    recordedAt: string;
  } | null;
  latestKills: {
    kills: number;
    recordedAt: string;
    source: string;
  } | null;
  pending: {
    id: number;
    m1Power: number;
    m1Type: MType;
    m2Power: number;
    m2Type: MType;
    m3Power: number;
    m3Type: MType;
    submittedAt: string;
  } | null;
  pendingList: Array<{
    id: number;
    m1Power: number;
    m1Type: MType;
    m2Power: number;
    m2Type: MType;
    m3Power: number;
    m3Type: MType;
    submittedAt: string;
  }>;
  history: Array<{
    id: number;
    m1Power: number;
    m1Type: MType;
    m2Power: number;
    m2Type: MType;
    m3Power: number;
    m3Type: MType;
    kills: number;
    recordedAt: string;
    source: string | null;
  }>;
  ranks: {
    m1: SelfReportRank;
    m2: SelfReportRank;
    m3: SelfReportRank;
    mSum: SelfReportRank;
    squad: SelfReportRank & { type: MType };
    kills: SelfReportRank;
  } | null;
  busterDay: {
    date: string;
    previousDate: string | null;
    previousKills: number;
    currentKills: number;
    deltaKills: number;
    referenceRecordedAt: string;
    fallback: boolean;
  } | null;
  busterRanking: {
    saturdayIso: string;
    entries: {
      position: number;
      player: { id: number; name: string; displayName: string | null; rank: string };
      deltaKills: number;
      isYou: boolean;
    }[];
  };
  comparison: {
    neighbors: {
      m1: NeighborPair;
      m2: NeighborPair;
      m3: NeighborPair;
      mSum: NeighborPair;
    };
    chaseQuips: {
      prey: string;
      hunter: string;
      top: string;
      rearClear: string;
    };
    distribution: {
      m1: number[];
      m2: number[];
      m3: number[];
      mSum: number[];
      own: { m1: number; m2: number; m3: number; mSum: number } | null;
      stats: {
        m1: { median: number | null; top10Avg: number | null; top25Avg: number | null };
        m2: { median: number | null; top10Avg: number | null; top25Avg: number | null };
        m3: { median: number | null; top10Avg: number | null; top25Avg: number | null };
        mSum: { median: number | null; top10Avg: number | null; top25Avg: number | null };
      };
    };
    trend: Array<{
      weekEnd: string;
      own: number | null;
      median: number | null;
      top10Avg: number | null;
      sampleSize: number;
    }>;
  };
  upcomingEvents: SelfReportUpcomingEvent[];
}

export interface SelfReportPendingRow {
  id: number;
  submittedAt: string;
  strengthRequestId: number | null;
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
  };
  values: {
    m1Power: number;
    m1Type: MType;
    m2Power: number;
    m2Type: MType;
    m3Power: number;
    m3Type: MType;
  };
  latestApproved: {
    m1Power: number;
    m2Power: number;
    m3Power: number;
    recordedAt: string;
  } | null;
}

export interface PushSubscriberRow {
  userId: number;
  username: string;
  role: Rank;
  active: boolean;
  deviceCount: number;
  lastUsedAt: string | null;
  lastCreatedAt: string | null;
  lastUa: string | null;
}

export interface SelfReportLinkRow {
  id: number;
  name: string;
  displayName: string | null;
  rank: Rank;
  token: string;
  autoApprove: boolean;
  submissionCount: number;
  lastSubmissionAt: string | null;
  pushDeviceCount: number;
}

export interface PushSendPayload {
  userIds?: number[];
  playerIds?: number[];
  title: string;
  body?: string;
  url?: string;
  tag?: string;
  silent?: boolean;
}

export interface SelfReportUpcomingEvent {
  eventId: number;
  type: 'canyon_storm' | 'desert_storm' | 'special_ingame' | 'train';
  eventDate: string;
  customName: string | null;
  slot: 'A' | 'B' | null;
  role: 'starter' | 'sub' | null;
  startsAtServer: string;
  startsAtUtc: string;
  teamIndex: number | null;
  teamName: string | null;
  teamTask: string | null;
  counterpartName?: string | null;
  counterpartRole?: 'starter' | 'sub' | null;
  trainKind?: TrainKind;
  conductor?: string;
  vipLabel?: string | null;
}

export type SelfReportRange = '30d' | '90d' | 'all';

export interface SelfReportMeEventCounts {
  registered: number;
  picked: number;
  notPicked: number;
  starter: number;
  sub: number;
  showed: number;
  noShow: number;
  late: number;
  slot: { A: number; B: number };
}

export interface SelfReportMeEventRow {
  eventId: number;
  type: 'canyon_storm' | 'desert_storm' | 'special_ingame';
  eventDate: string;
  customName: string | null;
  outcome: 'picked' | 'applied_not_picked' | 'not_applied' | 'upcoming';
  slot: 'A' | 'B' | null;
  role: 'starter' | 'sub' | null;
  attendance: 'showed' | 'late' | 'no_show' | 'rulebreaker' | null;
  teamIndex: number | null;
  teamName: string | null;
}

export interface SelfReportMeSpecialCounts {
  events: number;
  assigned: number;
  showed: number;
  rulebreaker: number;
  noShow: number;
  pending: number;
}

export interface SelfReportMeVsDay {
  dayIndex: number;
  scoreTenthsM: number | null;
  dayTwentiethTenthsM: number | null;
  dailyWin: boolean;
  rank: number | null;
  totalPlayers: number;
  othersAvgTenthsM: number | null;
}

export interface SelfReportMeVsWeek {
  id: number;
  weekStart: string;
  kind: VsKind;
  outcome: VsOutcome | null;
  opponentName: string;
  opponentServer: string;
  notes: string | null;
  weekScoreM: number | null;
  twentiethM: number | null;
  rank: number | null;
  totalPlayers: number;
  othersAvgM: number | null;
  days: SelfReportMeVsDay[] | null;
}

export interface SelfReportMe {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    timezone: string | null;
  };
  events: {
    range: SelfReportRange;
    totals: {
      canyon_storm: number;
      desert_storm: number;
      special_ingame: number;
    };
    counts: {
      canyon_storm: SelfReportMeEventCounts;
      desert_storm: SelfReportMeEventCounts;
    };
    special: SelfReportMeSpecialCounts;
    history: SelfReportMeEventRow[];
  };
  vs: {
    weeks: SelfReportMeVsWeek[];
    policy: VsPolicy;
  };
  upcomingEvents: SelfReportUpcomingEvent[];
}

export type PhotoAlbumStatus = 'open' | 'closed';

export interface PhotoAlbumCounts {
  playersWithPhoto: number;
  totalPlayers: number;
  freePhotos: number;
}

export interface PhotoAlbum {
  id: number;
  title: string;
  description: string | null;
  status: PhotoAlbumStatus;
  createdAt: string;
  createdBy: number | null;
  closedAt: string | null;
  closedBy: number | null;
  published: boolean;
  publicSlug: string | null;
  hasPassword: boolean;
  counts: PhotoAlbumCounts;
  access: { count: number; lastAt: string | null };
}

export interface PhotoAlbumAccessRow {
  id: number;
  at: string;
  ip: string | null;
  userAgent: string | null;
}

export interface PhotoAlbumEntryPlayer {
  id: number;
  name: string;
  displayName: string | null;
  realName: string | null;
  rank: Rank;
}

export interface PhotoAlbumEntry {
  entryId: number;
  player: PhotoAlbumEntryPlayer | null;
  title: string | null;
  hasPhoto: boolean;
  bio: string | null;
  focusX: number;
  focusY: number;
  sortOrder: number;
  declined: boolean;
  updatedAt: string | null;
  updatedBy: number | null;
}

export interface PublicBoardFlag {
  name: string;
  note: string | null;
  expiresAt: string | null;
}

export interface PublicBoardTrain {
  date: string;
  weekday: string;
  name: string | null;
}

export interface PublicBoard {
  alliance: AllianceBranding;
  weekStart: string;
  weekEnd: string;
  trains: PublicBoardTrain[];
  trainWeekNote: string | null;
  flags: {
    red: PublicBoardFlag[];
    yellow: PublicBoardFlag[];
    green: PublicBoardFlag[];
  };
  shouldBePicked: string[];
  recentNoShows: string[];
}

export interface PublicAlbumSummary {
  alliance: AllianceBranding;
  title: string;
  description: string | null;
  requiresPassword: boolean;
  hasAccess: boolean;
}

export interface PublicAlbumRow {
  entryId: number;
  playerId: number | null;
  name: string;
  realName: string | null;
  rank: Rank | null;
  bio: string | null;
  focusX: number;
  focusY: number;
  sortOrder: number;
  updatedAt: string;
}

export interface PublicAlbumDetail {
  album: {
    title: string;
    description: string | null;
  };
  rows: PublicAlbumRow[];
}

export interface PhotoAlbumDetail {
  album: PhotoAlbum;
  entries: PhotoAlbumEntry[];
  emptyPlayers: PhotoAlbumEntryPlayer[];
  declinedPlayers: PhotoAlbumEntryPlayer[];
}

export type TrainKind = 'regular' | 'mega_express';
export type TrainVipKind = 'defender' | 'guest';

export interface TrainAssignment {
  id: number;
  date: string;
  kind: TrainKind;
  notes: string | null;
  setAt: string | null;
  departsAt: string | null;
  score: number | null;
  createdAt: string;
  createdBy: number | null;
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
    active: boolean;
  };
  vip: {
    kind: TrainVipKind | null;
    player: {
      id: number;
      name: string;
      displayName: string | null;
      rank: Rank;
    };
  } | null;
}

export interface TrainAssignmentBody {
  playerId: number;
  notes?: string | null;
  setAt?: string | null;
  score?: number | null;
  vipPlayerId?: number | null;
  vipKind?: TrainVipKind | null;
}

export interface TrainStatsRow {
  playerId: number;
  name: string;
  displayName: string | null;
  rank: Rank;
  active: boolean;
  count: number;
  lastDate: string;
}

export interface TrainWeekNote {
  weekStart: string;
  note: string;
  updatedAt: string;
  updatedBy: number | null;
}

export interface TrainHistoryEntry {
  date: string;
  notes: string | null;
  createdAt: string;
  createdBy: number | null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  // Only declare a JSON Content-Type when we actually send a body; otherwise
  // Fastify rejects with "Body cannot be empty when content-type is set to 'application/json'".
  // FormData must NOT get an explicit Content-Type — the browser sets
  // "multipart/form-data; boundary=…" on its own; overriding it strips the
  // boundary and the server can't parse the request.
  if (
    init?.body != null &&
    !headers['Content-Type'] &&
    !(typeof FormData !== 'undefined' && init.body instanceof FormData)
  ) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.error ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, message: string | object) {
    super(typeof message === 'string' ? message : JSON.stringify(message));
  }
}

export const api = {
  login: (username: string, password: string) =>
    request<AuthUser>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<AuthUser>('/api/auth/me'),
  stopImpersonation: () =>
    request<{ ok: true }>('/api/auth/stop-impersonation', { method: 'POST' }),

  getAllianceSettings: () => request<AllianceSettings>('/api/alliance'),
  updateAllianceSettings: (body: {
    name?: string;
    tag?: string | null;
    slug?: string;
    motto?: string | null;
    lastrankUrl?: string | null;
  }) =>
    request<AllianceSettings>('/api/alliance', { method: 'PATCH', body: JSON.stringify(body) }),
  uploadAllianceLogo: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<AllianceBranding>('/api/alliance/logo', { method: 'POST', body: fd });
  },
  deleteAllianceLogo: () =>
    request<AllianceBranding>('/api/alliance/logo', { method: 'DELETE' }),
  getPublicAllianceBranding: (slug: string) =>
    request<AllianceBranding>(`/api/public/alliances/${encodeURIComponent(slug)}`),
  getPlayerAllianceBranding: (token: string) =>
    request<AllianceBranding>(`/api/public/players/${encodeURIComponent(token)}/alliance`),

  adminListServers: () => request<ServerRow[]>('/api/admin/servers'),
  adminCreateServer: (body: { number: number; name?: string | null }) =>
    request<ServerRow>('/api/admin/servers', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateServer: (id: number, body: { number?: number; name?: string | null }) =>
    request<ServerRow>(`/api/admin/servers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  adminListTenants: () => request<TenantRow[]>('/api/admin/tenants'),
  adminCreateTenant: (body: {
    serverId: number;
    name: string;
    tag?: string | null;
    slug?: string;
    admin: { username: string; password: string };
  }) => request<unknown>('/api/admin/tenants', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateTenant: (
    id: number,
    body: { name?: string; tag?: string | null; slug?: string; active?: boolean; serverId?: number },
  ) =>
    request<unknown>(`/api/admin/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminSetTenantFeatures: (id: number, features: AllianceFeature[]) =>
    request<{ features: AllianceFeature[] }>(`/api/admin/tenants/${id}/features`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    }),
  adminListUsers: () => request<AdminUserRow[]>('/api/admin/users'),
  adminUpdateUser: (
    id: number,
    body: {
      isSuperadmin?: boolean;
      isServerAdmin?: boolean;
      allianceId?: number | null;
      role?: Rank;
      active?: boolean;
      password?: string;
    },
  ) =>
    request<unknown>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminImpersonate: (id: number) =>
    request<{ ok: true }>(`/api/admin/impersonate/${id}`, { method: 'POST' }),

  playerSession: () =>
    request<{ token: string; playerId: number; label: string }>(
      '/api/player-session',
    ),
  myPlayerLink: () =>
    request<{ token: string | null; playerId?: number; label?: string }>(
      '/api/auth/my-player-link',
    ),
  playerSessionLogout: () =>
    request<{ ok: true }>('/api/player-session/logout', { method: 'POST' }),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listPlayers: (opts?: { includeInactive?: boolean }) =>
    request<Player[]>(`/api/players${opts?.includeInactive ? '?includeInactive=true' : ''}`),
  getPlayer: (id: number) => request<Player>(`/api/players/${id}`),
  createPlayer: (body: Partial<Player>) =>
    request<Player>('/api/players', { method: 'POST', body: JSON.stringify(body) }),
  updatePlayer: (id: number, body: Partial<Player> & { comment?: string | null }) =>
    request<Player>(`/api/players/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  listPlayerMoveTargets: () => request<PlayerMoveTarget[]>('/api/players/move-targets'),
  movePlayer: (id: number, body: { targetAllianceId: number; comment?: string | null }) =>
    request<{ targetPlayerId: number; merged: boolean; snapshotsCopied: number }>(
      `/api/players/${id}/move`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  deactivatePlayer: (id: number, comment?: string | null) =>
    request<Player>(`/api/players/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment ?? null }),
    }),
  reactivatePlayer: (id: number, comment?: string | null) =>
    request<Player>(`/api/players/${id}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment ?? null }),
    }),
  deletePlayer: (id: number, comment?: string | null) =>
    request<Player>(`/api/players/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment ?? null }),
    }),
  undeletePlayer: (id: number) =>
    request<Player>(`/api/players/${id}/undelete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listSnapshots: (id: number) => request<PowerSnapshot[]>(`/api/players/${id}/snapshots`),
  addSnapshot: (id: number, body: Omit<PowerSnapshot, 'id' | 'playerId' | 'recordedAt' | 'recordedBy'>) =>
    request<PowerSnapshot>(`/api/players/${id}/snapshots`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteSnapshot: (snapshotId: number) =>
    request<{ ok: true }>(`/api/snapshots/${snapshotId}`, { method: 'DELETE' }),

  listLeaves: (id: number) => request<LeavePeriod[]>(`/api/players/${id}/leaves`),
  addLeave: (
    id: number,
    body: { startsAt: string; endsAt?: string | null; reason?: string },
  ) =>
    request<LeavePeriod>(`/api/players/${id}/leaves`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateLeave: (id: number, body: Partial<LeavePeriod>) =>
    request<LeavePeriod>(`/api/leaves/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteLeave: (id: number) => request<{ ok: true }>(`/api/leaves/${id}`, { method: 'DELETE' }),

  listEvents: (params?: { type?: EventType; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.type) q.set('type', params.type);
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const qs = q.toString();
    return request<EventSummary[]>(`/api/events${qs ? `?${qs}` : ''}`);
  },
  listThisWeek: () => request<EventSummary[]>('/api/events/this-week'),
  listPendingAttendance: () =>
    request<{ id: number; type: EventType; eventDate: string }[]>(
      '/api/events/pending-attendance',
    ),
  getEvent: (id: number) => request<EventSummary>(`/api/events/${id}`),
  createEvent: (body: {
    type: EventType;
    eventDate: string;
    slots: { slotKey: SlotKey; startsAtServer: string }[];
  }) => request<EventSummary>('/api/events', { method: 'POST', body: JSON.stringify(body) }),
  patchEvent: (
    id: number,
    body: Partial<{ status: EventStatus; planningDone: boolean; slots: EventSlot[] }>,
  ) =>
    request<EventSummary>(`/api/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteEvent: (id: number) =>
    request<{ ok: true }>(`/api/events/${id}`, { method: 'DELETE' }),

  createSpecialEvent: (body: {
    customName: string;
    eventDate: string;
    startsAt?: string | null;
    teams: {
      name?: string | null;
      capacity: number;
      strengthShare: number;
      leaderPlayerId?: number | null;
      task?: string | null;
    }[];
  }) =>
    request<SpecialEventDetail>('/api/events/special', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSpecialEvent: (id: number) =>
    request<SpecialEventDetail>(`/api/events/${id}/special`),
  patchSpecialEvent: (
    id: number,
    body: Partial<{
      customName: string;
      eventDate: string;
      startsAt: string | null;
      status: EventStatus;
      planningDone: boolean;
    }>,
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/special`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addSpecialTeam: (
    id: number,
    body: {
      name?: string | null;
      capacity: number;
      strengthShare: number;
      leaderPlayerId?: number | null;
      task?: string | null;
    },
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/teams`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchSpecialTeam: (
    id: number,
    teamId: number,
    body: Partial<{
      name: string | null;
      capacity: number;
      strengthShare: number;
      leaderPlayerId: number | null;
      task: string | null;
    }>,
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteSpecialTeam: (id: number, teamId: number) =>
    request<SpecialEventDetail>(`/api/events/${id}/teams/${teamId}`, {
      method: 'DELETE',
    }),
  upsertSpecialAssignment: (
    id: number,
    body: {
      teamId: number;
      position: number;
      playerId: number | null;
      locked?: boolean;
      notes?: string | null;
    },
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  moveSpecialAssignment: (
    id: number,
    body: {
      fromTeamId: number;
      fromPosition: number;
      toTeamId: number;
      toPosition: number;
    },
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/assignments/move`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  clearSpecialAssignments: (id: number) =>
    request<SpecialEventDetail>(`/api/events/${id}/clear-assignments`, {
      method: 'POST',
      body: '{}',
    }),
  distributeSpecialEvent: (id: number, body: { mode: 'fill' | 'force' }, dryRun = false) =>
    request<SpecialEventDetail & { overflow?: number[] }>(
      `/api/events/${id}/distribute${dryRun ? '?dryRun=1' : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  setSpecialAttendance: (
    id: number,
    rows: { assignmentId: number; attendance: SpecialAttendance | null }[],
  ) =>
    request<SpecialEventDetail>(`/api/events/${id}/special-attendance`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),

  listParticipations: (eventId: number) =>
    request<Participation[]>(`/api/events/${eventId}/participations`),
  bulkParticipations: (
    eventId: number,
    rows: Array<{
      playerId: number;
      appliedA?: boolean;
      appliedB?: boolean;
      assignedSlot?: SlotKey | null;
      assignedRole?: AssignedRole | null;
      assignedPosition?: number | null;
      flex?: boolean;
      notes?: string | null;
    }>,
    source?: string,
  ) =>
    request<{ ok: true }>(`/api/events/${eventId}/participations`, {
      method: 'POST',
      body: JSON.stringify(source ? { rows, source } : { rows }),
    }),
  patchParticipation: (
    eventId: number,
    playerId: number,
    body: {
      appliedA?: boolean;
      appliedB?: boolean;
      assignedSlot?: SlotKey | null;
      assignedRole?: AssignedRole | null;
      assignedPosition?: number | null;
      flex?: boolean;
      notes?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/api/events/${eventId}/participations/${playerId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteParticipation: (eventId: number, playerId: number) =>
    request<{ ok: true }>(`/api/events/${eventId}/participations/${playerId}`, {
      method: 'DELETE',
    }),
  bulkAttendance: (
    eventId: number,
    rows: { playerId: number; attendanceStatus: AttendanceStatus | null }[],
  ) =>
    request<{ ok: true }>(`/api/events/${eventId}/attendance`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  candidates: (eventId: number, slot: SlotKey) =>
    request<Candidate[]>(`/api/events/${eventId}/candidates?slot=${slot}`),
  importSignupsPreview: (eventId: number, payload: OcrImportPayload) =>
    request<ImportPreviewResponse>(`/api/events/${eventId}/signups/import`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  importSignupsConfirm: (eventId: number, slot: SlotKey, rows: ImportConfirmRow[]) =>
    request<ImportConfirmResponse>(`/api/events/${eventId}/signups/import/confirm`, {
      method: 'POST',
      body: JSON.stringify({ slot, rows }),
    }),
  getEventStats: (params: {
    type?: EventType | 'all';
    range?: EventStatsRange;
    activeOnly?: boolean;
    historyLimit?: EventStatsHistoryLimit;
  }) => {
    const q = new URLSearchParams();
    if (params.type && params.type !== 'all') q.set('type', params.type);
    if (params.range && params.range !== 'all') q.set('range', params.range);
    if (params.activeOnly === false) q.set('activeOnly', '0');
    if (params.historyLimit) q.set('historyLimit', String(params.historyLimit));
    const qs = q.toString();
    return request<EventStatsResponse>(`/api/events/stats${qs ? `?${qs}` : ''}`);
  },
  getSpecialEventStats: (params: {
    range?: EventStatsRange;
    activeOnly?: boolean;
    historyLimit?: EventStatsHistoryLimit;
  }) => {
    const q = new URLSearchParams();
    if (params.range && params.range !== 'all') q.set('range', params.range);
    if (params.activeOnly === false) q.set('activeOnly', '0');
    if (params.historyLimit) q.set('historyLimit', String(params.historyLimit));
    const qs = q.toString();
    return request<SpecialEventStatsResponse>(
      `/api/events/stats/special${qs ? `?${qs}` : ''}`,
    );
  },
  uploadSignupsVideo: async (
    eventId: number,
    slot: SlotKey,
    file: File,
  ): Promise<ImportVideoUploadResponse> => {
    const fd = new FormData();
    fd.append('slot', slot);
    fd.append('video', file, file.name);
    const res = await fetch(`/api/events/${eventId}/signups/import-video`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body?.error ?? res.statusText);
    }
    return (await res.json()) as ImportVideoUploadResponse;
  },
  getImportVideoJob: (eventId: number, jobId: string) =>
    request<ImportVideoJobState>(`/api/events/${eventId}/signups/import-video-job/${jobId}`),
  listEventImportJobs: (eventId: number) =>
    request<{ jobs: ImportVideoJobState[] }>(`/api/events/${eventId}/signups/import-jobs`),
  eventImportJobsSummary: () =>
    request<{
      byEvent: Record<number, { pending: number; ready: number; failed: number }>;
    }>(`/api/events/import-jobs-summary`),
  deleteEventImportJob: (eventId: number, jobId: string) =>
    request<void>(`/api/events/${eventId}/signups/import-jobs/${jobId}`, { method: 'DELETE' }),
  retryEventImportJob: (eventId: number, jobId: string) =>
    request<ImportVideoJobState>(
      `/api/events/${eventId}/signups/import-jobs/${jobId}/retry`,
      { method: 'POST' },
    ),
  playerHistory: (playerId: number, limit = 10) =>
    request<{ events: ParticipationHistoryItem[]; metrics: PlayerMetrics | null }>(
      `/api/players/${playerId}/participation-history?limit=${limit}`,
    ),
  dashboardStats: () => request<DashboardStats>('/api/dashboard/stats'),
  startLastRankSync: () =>
    request<SyncJobState>('/api/sync/lastrank', { method: 'POST' }),
  backfillTimezones: () =>
    request<{ updated: number; skipped: { id: number; country: string }[] }>(
      '/api/sync/backfill-timezones',
      { method: 'POST' },
    ),
  syncStatus: () => request<SyncStatus>('/api/sync/status'),
  syncPlayer: (playerId: number) =>
    request<{
      ok: true;
      result: {
        matched?: { playerId: number };
        nameChange?: { playerId: number; oldName: string; newName: string };
        rankChange?: { playerId: number; name: string; oldRank: string; newRank: string };
        professionUpdate?: { playerId: number; name: string; profession: string | null };
        snapshotCreated?: boolean;
      };
    }>(`/api/sync/player/${playerId}`, { method: 'POST' }),
  listNameHistory: (playerId: number) =>
    request<NameHistoryEntry[]>(`/api/players/${playerId}/name-history`),
  listPlayerChangeLog: (playerId: number) =>
    request<PlayerChangeLogEntry[]>(`/api/players/${playerId}/changelog`),
  listSnapshotBaselines: (since: string) =>
    request<SnapshotBaseline[]>(`/api/snapshots/baselines?since=${since}`),
  listEventChangeLog: (eventId: number) =>
    request<EventChangeLogEntry[]>(`/api/events/${eventId}/changelog`),

  listSurveys: () => request<Survey[]>('/api/surveys'),
  getSurvey: (id: number) => request<SurveyDetail>(`/api/surveys/${id}`),
  createSurvey: (body: {
    title: string;
    description?: string | null;
    category?: SurveyCategory;
  }) =>
    request<Survey>('/api/surveys', { method: 'POST', body: JSON.stringify(body) }),
  updateSurvey: (
    id: number,
    body: {
      title?: string;
      description?: string | null;
      category?: SurveyCategory;
      status?: SurveyStatus;
    },
  ) =>
    request<Survey>(`/api/surveys/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSurvey: (id: number) =>
    request<{ ok: true }>(`/api/surveys/${id}`, { method: 'DELETE' }),
  setSurveyResponse: (
    id: number,
    playerId: number,
    body: { answer: SurveyAnswer; notes?: string | null },
  ) =>
    request<unknown>(`/api/surveys/${id}/responses/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  listStrengthRequests: () =>
    request<StrengthRequest[]>('/api/strength-requests'),
  getStrengthRequest: (id: number) =>
    request<StrengthRequestDetail>(`/api/strength-requests/${id}`),
  createStrengthRequest: (body: {
    title: string;
    startedAt?: string;
    closedAt?: string;
  }) =>
    request<StrengthRequest>('/api/strength-requests', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateStrengthRequest: (
    id: number,
    body: { title?: string; status?: StrengthRequestStatus },
  ) =>
    request<StrengthRequest>(`/api/strength-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteStrengthRequest: (id: number) =>
    request<{ ok: true }>(`/api/strength-requests/${id}`, { method: 'DELETE' }),
  addStrengthRequestEntry: (
    id: number,
    body: {
      playerId: number;
      m1Power: number;
      m1Type: MType;
      m2Power: number;
      m2Type: MType;
      m3Power: number;
      m3Type: MType;
    },
  ) =>
    request<PowerSnapshot>(`/api/strength-requests/${id}/entries`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getStrengthStats: (params: { startDate: string; endDate: string }) => {
    const q = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });
    return request<StrengthStats>(`/api/strength-requests/stats?${q.toString()}`);
  },
  getStrengthResponders: (id: number) =>
    request<StrengthResponders>(`/api/strength-requests/${id}/responders`),

  getEventSwap: (token: string, eventId: number) =>
    request<EventSwapPayload>(
      `/api/players/${encodeURIComponent(token)}/events/${eventId}/swap`,
    ),
  patchEventSwap: (
    token: string,
    eventId: number,
    body: { contactMade?: boolean; aim15Min?: boolean },
  ) =>
    request<EventSwapPayload>(
      `/api/players/${encodeURIComponent(token)}/events/${eventId}/swap`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  getSelfReportSummary: (token: string) =>
    request<SelfReportSummary>(`/api/self-report/${encodeURIComponent(token)}`),
  getSelfReportMe: (token: string, range: SelfReportRange = '90d') =>
    request<SelfReportMe>(
      `/api/self-report/${encodeURIComponent(token)}/me?range=${range}`,
    ),
  submitSelfReport: (
    token: string,
    body: {
      m1Power: number;
      m1Type: MType;
      m2Power: number;
      m2Type: MType;
      m3Power: number;
      m3Type: MType;
    },
  ) =>
    request<{ ok: true; autoApproved: boolean; submittedAt: string }>(
      `/api/self-report/${encodeURIComponent(token)}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  submitSelfReportKills: (token: string, kills: number) =>
    request<{ ok: true; autoApproved: true; submittedAt: string; kills: number }>(
      `/api/self-report/${encodeURIComponent(token)}/kills`,
      { method: 'POST', body: JSON.stringify({ kills }) },
    ),
  setSelfReportAutoApprove: (playerId: number, autoApprove: boolean) =>
    request<{ ok: true; autoApprove: boolean }>(
      `/api/players/${playerId}/self-report-auto-approve`,
      { method: 'PATCH', body: JSON.stringify({ autoApprove }) },
    ),
  createSelfReportToken: (playerId: number) =>
    request<{ token: string }>(`/api/players/${playerId}/self-report-token`, {
      method: 'POST',
    }),
  deleteSelfReportToken: (playerId: number) =>
    request<{ ok: true }>(`/api/players/${playerId}/self-report-token`, {
      method: 'DELETE',
    }),
  listPendingSelfReports: () =>
    request<{ rows: SelfReportPendingRow[] }>(`/api/self-reports/pending`),
  approveSelfReport: (id: number) =>
    request<{ ok: true; snapshotId: number }>(`/api/self-reports/${id}/approve`, {
      method: 'POST',
    }),
  rejectSelfReport: (id: number) =>
    request<{ ok: true }>(`/api/self-reports/${id}/reject`, { method: 'POST' }),
  bulkSelfReports: (action: 'approve' | 'reject', ids: number[]) =>
    request<{ ok: true; processed: number; snapshotIds: number[] }>(
      `/api/self-reports/bulk`,
      { method: 'POST', body: JSON.stringify({ action, ids }) },
    ),
  listSelfReportLinks: () =>
    request<{ rows: SelfReportLinkRow[] }>(`/api/players/self-report-links`),

  getPushVapidKey: () =>
    request<{ publicKey: string }>(`/api/push/vapid-public-key`),
  pushSubscribe: (body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    ua?: string;
  }) =>
    request<{ ok: true }>(`/api/push/subscribe`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>(`/api/push/unsubscribe`, {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
  listPushSubscribers: () =>
    request<{ rows: PushSubscriberRow[] }>(`/api/push/subscribers`),
  sendPushTest: (userId: number) =>
    request<{ ok: true; sentTo: number }>(`/api/push/test`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  sendPush: (payload: PushSendPayload) =>
    request<{ ok: true; users: number; players: number; devices: number }>(
      `/api/push/send`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),

  listPhotoAlbums: () => request<PhotoAlbum[]>('/api/photo-albums'),
  getPhotoAlbum: (id: number) =>
    request<PhotoAlbumDetail>(`/api/photo-albums/${id}`),
  getPhotoAlbumAccess: (id: number) =>
    request<{ rows: PhotoAlbumAccessRow[] }>(`/api/photo-albums/${id}/access`),
  createPhotoAlbum: (body: { title: string; description?: string | null }) =>
    request<PhotoAlbum>('/api/photo-albums', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updatePhotoAlbum: (
    id: number,
    body: {
      title?: string;
      description?: string | null;
      status?: PhotoAlbumStatus;
      published?: boolean;
      publicSlug?: string | null;
      password?: string | null;
    },
  ) =>
    request<PhotoAlbum>(`/api/photo-albums/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deletePhotoAlbum: (id: number) =>
    request<{ ok: true }>(`/api/photo-albums/${id}`, { method: 'DELETE' }),
  updatePhotoAlbumEntry: (
    id: number,
    playerId: number,
    body: {
      bio?: string | null;
      focusX?: number;
      focusY?: number;
      declined?: boolean;
    },
  ) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/${playerId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  setPhotoAlbumEntryDeclined: (id: number, playerId: number, declined: boolean) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/${playerId}`,
      { method: 'PATCH', body: JSON.stringify({ declined }) },
    ),
  uploadPhotoAlbumEntryPhoto: async (
    id: number,
    playerId: number,
    file: File,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(
      `/api/photo-albums/${id}/entries/${playerId}/photo`,
      { method: 'POST', credentials: 'include', body: fd },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body?.error ?? res.statusText);
    }
    return res.json() as Promise<{ ok: true; size: number; mime: string }>;
  },
  deletePhotoAlbumEntryPhoto: (id: number, playerId: number) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/${playerId}/photo`,
      { method: 'DELETE' },
    ),
  photoAlbumEntryPhotoUrl: (id: number, playerId: number, cacheKey?: string) =>
    `/api/photo-albums/${id}/entries/${playerId}/photo${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  photoAlbumEntryPhotoUrlById: (id: number, entryId: number, cacheKey?: string) =>
    `/api/photo-albums/${id}/entries/id/${entryId}/photo${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,

  createFreePhotoAlbumEntry: (id: number, body: { title?: string | null }) =>
    request<{ id: number; sortOrder: number }>(
      `/api/photo-albums/${id}/free-entries`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  patchPhotoAlbumEntryById: (
    id: number,
    entryId: number,
    body: {
      bio?: string | null;
      title?: string | null;
      focusX?: number;
      focusY?: number;
      sortOrder?: number;
    },
  ) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/id/${entryId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  uploadPhotoAlbumEntryPhotoById: async (id: number, entryId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(
      `/api/photo-albums/${id}/entries/id/${entryId}/photo`,
      { method: 'POST', credentials: 'include', body: fd },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body?.error ?? res.statusText);
    }
    return res.json() as Promise<{ ok: true; size: number }>;
  },
  deletePhotoAlbumEntryPhotoById: (id: number, entryId: number) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/id/${entryId}/photo`,
      { method: 'DELETE' },
    ),
  deletePhotoAlbumFreeEntry: (id: number, entryId: number) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/id/${entryId}`,
      { method: 'DELETE' },
    ),
  reassignPhotoAlbumEntry: async (
    id: number,
    entryId: number,
    toPlayerId: number,
    allowSwap = false,
  ) => {
    const res = await fetch(`/api/photo-albums/${id}/entries/id/${entryId}/reassign`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toPlayerId, allowSwap }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new ApiError(res.status, body?.message ?? body?.error ?? res.statusText);
      (err as ApiError & { conflictEntryId?: number; conflictPlayer?: unknown }).conflictEntryId =
        body?.conflictEntryId;
      (err as ApiError & { conflictEntryId?: number; conflictPlayer?: unknown }).conflictPlayer =
        body?.conflictPlayer;
      throw err;
    }
    return body as { ok: true; swapped?: boolean; swappedWithEntryId?: number; unchanged?: boolean };
  },
  reassignPhotoAlbumEntriesBatch: async (
    id: number,
    moves: Array<{ entryId: number; toPlayerId: number | null }>,
  ) => {
    const res = await fetch(`/api/photo-albums/${id}/entries/reassign-batch`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ moves }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new ApiError(res.status, body?.message ?? body?.error ?? res.statusText);
    }
    return body as { ok: true; applied: number };
  },
  reorderPhotoAlbumEntries: (id: number, entryIds: number[]) =>
    request<{ ok: true }>(
      `/api/photo-albums/${id}/entries/order`,
      { method: 'PUT', body: JSON.stringify({ entryIds }) },
    ),

  getPublicBoard: (slug?: string) =>
    request<PublicBoard>(
      slug ? `/api/public/board?alliance=${encodeURIComponent(slug)}` : '/api/public/board',
    ),

  getPublicAlbumSummary: (slug: string) =>
    request<PublicAlbumSummary>(`/api/public/albums/${encodeURIComponent(slug)}/summary`),
  getPublicAlbum: (slug: string) =>
    request<PublicAlbumDetail>(`/api/public/albums/${encodeURIComponent(slug)}`),
  verifyPublicAlbum: (slug: string, password: string) =>
    request<{ ok: true }>(
      `/api/public/albums/${encodeURIComponent(slug)}/verify`,
      { method: 'POST', body: JSON.stringify({ password }) },
    ),
  publicAlbumPhotoUrl: (slug: string, playerId: number, cacheKey?: string) =>
    `/api/public/albums/${encodeURIComponent(slug)}/photo/${playerId}${
      cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''
    }`,
  publicAlbumPhotoUrlByEntry: (slug: string, entryId: number, cacheKey?: string) =>
    `/api/public/albums/${encodeURIComponent(slug)}/photo-by-entry/${entryId}${
      cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''
    }`,

  listTrains: (range?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (range?.from) q.set('from', range.from);
    if (range?.to) q.set('to', range.to);
    const qs = q.toString();
    return request<TrainAssignment[]>(`/api/trains${qs ? `?${qs}` : ''}`);
  },
  getTrainStats: (range?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (range?.from) q.set('from', range.from);
    if (range?.to) q.set('to', range.to);
    const qs = q.toString();
    return request<TrainStatsRow[]>(`/api/trains/stats${qs ? `?${qs}` : ''}`);
  },
  setTrainAssignment: (
    date: string,
    body: TrainAssignmentBody & { kind?: TrainKind },
  ) =>
    request<{ ok: true }>(`/api/trains/${date}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  createMegaExpressTrain: (
    body: TrainAssignmentBody & { date: string },
  ) =>
    request<{ ok: true }>(`/api/trains`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  clearTrainAssignment: (date: string, kind: TrainKind = 'regular') =>
    request<void>(
      `/api/trains/${date}${kind !== 'regular' ? `?kind=${kind}` : ''}`,
      { method: 'DELETE' },
    ),
  updateTrainAssignmentById: (id: number, body: TrainAssignmentBody) =>
    request<{ ok: true }>(`/api/trains/id/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  clearTrainAssignmentById: (id: number) =>
    request<void>(`/api/trains/id/${id}`, { method: 'DELETE' }),
  listTrainWeekNotes: (range?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (range?.from) q.set('from', range.from);
    if (range?.to) q.set('to', range.to);
    const qs = q.toString();
    return request<TrainWeekNote[]>(
      `/api/trains/week-notes${qs ? `?${qs}` : ''}`,
    );
  },
  setTrainWeekNote: (weekStart: string, note: string) =>
    request<{ ok: true } | void>(`/api/trains/week-notes/${weekStart}`, {
      method: 'PUT',
      body: JSON.stringify({ note }),
    }),
  getTrainHistory: (playerId: number) =>
    request<TrainHistoryEntry[]>(`/api/trains/history/${playerId}`),

  listActiveFlags: () => request<ActivePlayerFlag[]>('/api/flags/active'),
  listPlayerFlags: (playerId: number) =>
    request<{ active: PlayerFlag[]; history: PlayerFlag[] }>(
      `/api/players/${playerId}/flags`,
    ),
  createPlayerFlag: (
    playerId: number,
    body: { kind: PlayerFlagKind; note?: string | null; expiresAt?: string | null },
  ) =>
    request<PlayerFlag>(`/api/players/${playerId}/flags`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revokePlayerFlag: (playerId: number, flagId: number) =>
    request<void>(`/api/players/${playerId}/flags/${flagId}`, { method: 'DELETE' }),

  listUsers: () => request<UserRow[]>('/api/users'),
  createUser: (body: { username: string; password: string; role: Rank; playerId?: number | null }) =>
    request<UserRow>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (
    id: number,
    body: {
      username?: string;
      password?: string;
      role?: Rank;
      playerId?: number | null;
      active?: boolean;
    },
  ) => request<UserRow>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  listAlliances: () => request<Alliance[]>('/api/alliances'),
  createAlliance: (body: {
    name: string;
    tag?: string | null;
    notes?: string | null;
    active?: boolean;
  }) => request<Alliance>('/api/alliances', { method: 'POST', body: JSON.stringify(body) }),
  updateAlliance: (
    id: number,
    body: { name?: string; tag?: string | null; notes?: string | null; active?: boolean },
  ) =>
    request<Alliance>(`/api/alliances/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAlliance: (id: number) =>
    request<void>(`/api/alliances/${id}`, { method: 'DELETE' }),

  listTransferWindows: () => request<TransferWindowRow[]>('/api/transfer-windows'),
  listOpenTransferWindows: () =>
    request<TransferWindowOpen[]>('/api/transfer-windows/open'),
  getTransferWindow: (id: number) =>
    request<TransferWindowDetail>(`/api/transfer-windows/${id}`),
  createTransferWindow: (body: {
    name: string;
    notes?: string | null;
    seatsGold?: number | null;
    seatsPurple?: number | null;
    seatsBlue?: number | null;
    seatsWhite?: number | null;
  }) =>
    request<TransferWindowRow>('/api/transfer-windows', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTransferWindow: (
    id: number,
    body: {
      name?: string;
      notes?: string | null;
      seatsGold?: number | null;
      seatsPurple?: number | null;
      seatsBlue?: number | null;
      seatsWhite?: number | null;
      closed?: boolean;
    },
  ) =>
    request<TransferWindowRow>(`/api/transfer-windows/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTransferWindow: (id: number) =>
    request<void>(`/api/transfer-windows/${id}`, { method: 'DELETE' }),
  addTransferPlayer: (windowId: number, body: TransferPlayerInput) =>
    request<TransferPlayer>(`/api/transfer-windows/${windowId}/players`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTransferPlayer: (id: number, body: Partial<TransferPlayerInput>) =>
    request<TransferPlayer>(`/api/transfer-players/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTransferPlayer: (id: number) =>
    request<void>(`/api/transfer-players/${id}`, { method: 'DELETE' }),
  bulkCreateLeavers: (windowId: number, playerIds: number[]) =>
    request<{ created: number; skipped: number }>(
      `/api/transfer-windows/${windowId}/leavers/bulk`,
      { method: 'POST', body: JSON.stringify({ playerIds }) },
    ),

  getKickWeights: () => request<KickWeights>('/api/kick-weights'),
  updateKickWeights: (body: Partial<KickWeights>) =>
    request<KickWeights>('/api/kick-weights', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getKickCandidates: (windowId: number) =>
    request<{ weights: KickWeights; players: KickCandidate[] }>(
      `/api/transfer-windows/${windowId}/kick-candidates`,
    ),
  markKickCandidates: (windowId: number, playerIds: number[]) =>
    request<{ created: number; skipped: number }>(
      `/api/transfer-windows/${windowId}/kick-candidates/mark`,
      { method: 'POST', body: JSON.stringify({ playerIds }) },
    ),

  listVs: () => request<VsWeekRow[]>('/api/vs'),
  getVs: (id: number) => request<VsDetail>(`/api/vs/${id}`),
  suggestVsOpponents: () =>
    request<{ opponents: VsOpponentSuggestion[] }>('/api/vs/suggest'),
  createVs: (body: VsInput) =>
    request<VsWeek>('/api/vs', { method: 'POST', body: JSON.stringify(body) }),
  updateVs: (
    id: number,
    body: Partial<VsInput> & { locked?: boolean; outcome?: VsOutcome | null },
  ) =>
    request<VsWeek>(`/api/vs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteVs: (id: number) => request<void>(`/api/vs/${id}`, { method: 'DELETE' }),
  saveVsScores: (
    id: number,
    entries: Array<{ playerId: number; scoreM: number | null }>,
  ) =>
    request<{ scores: VsPlayerScore[] }>(`/api/vs/${id}/scores`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),
  saveVsTopPlayers: (
    id: number,
    entries: Array<{ dayIndex: number; playerId: number | null }>,
  ) =>
    request<{ topPlayers: VsTopPlayerEntry[] }>(`/api/vs/${id}/top-players`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),
  getCurrentVs: () => request<VsCurrent | null>('/api/public/vs/current'),
  getVsOverview: () => request<VsOverview>('/api/vs/overview'),
  getVsPolicy: () => request<VsPolicy>('/api/vs/policy'),
  updateVsPolicy: (body: Partial<VsPolicy>) =>
    request<VsPolicy>('/api/vs/policy', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  saveVsDailyScores: (
    id: number,
    entries: Array<{ playerId: number; dayIndex: number; scoreTenthsM: number | null }>,
  ) =>
    request<{ dailyScores: VsDailyScore[] }>(`/api/vs/${id}/daily-scores`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),
  uploadVsImportVideo: (
    id: number,
    file: File,
    kind: 'weekly' | 'daily',
    dayIndex: number | null,
  ) => {
    const body = new FormData();
    body.append('file', file);
    body.append('kind', kind);
    if (dayIndex != null) body.append('day', String(dayIndex));
    return request<{ jobId: string; bytes: number; kind: 'weekly' | 'daily'; dayIndex: number | null }>(
      `/api/vs/${id}/import-video`,
      { method: 'POST', body },
    );
  },
  getVsImportVideoJob: (id: number, jobId: string) =>
    request<VsImportVideoJob>(`/api/vs/${id}/import-video-job/${jobId}`),
  listVsImportJobs: (id: number) =>
    request<{ jobs: VsImportVideoJob[] }>(`/api/vs/${id}/import-jobs`),
  vsImportJobsSummary: () =>
    request<{
      byWeek: Record<number, { pending: number; ready: number; failed: number }>;
    }>(`/api/vs/import-jobs-summary`),
  pickVsImportKind: (id: number, jobId: string, body: { kind: 'weekly' | 'daily'; dayIndex: number | null }) =>
    request<VsImportVideoJob>(`/api/vs/${id}/import-video-job/${jobId}/pick`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteVsImportJob: (id: number, jobId: string) =>
    request<void>(`/api/vs/${id}/import-jobs/${jobId}`, { method: 'DELETE' }),
  retryVsImportJob: (id: number, jobId: string) =>
    request<VsImportVideoJob>(`/api/vs/${id}/import-jobs/${jobId}/retry`, {
      method: 'POST',
    }),
  previewVsImport: (
    id: number,
    payload: {
      kind: 'weekly' | 'daily';
      dayIndex: number | null;
      rows: Array<{ name: string; scoreTenthsM: number }>;
    },
  ) =>
    request<VsImportPreview>(`/api/vs/${id}/import-video/preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // --- Kills video import (Plan 038) -----------------------------------
  uploadKillsImportVideo: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ jobId: string; bytes: number }>(`/api/kills/import-video`, {
      method: 'POST',
      body,
    });
  },
  getKillsImportJob: (jobId: string) =>
    request<KillImportJob>(`/api/kills/import-jobs/${jobId}`),
  listKillsImportJobs: () =>
    request<{ jobs: KillImportJob[] }>(`/api/kills/import-jobs`),
  retryKillsImportJob: (jobId: string) =>
    request<KillImportJob>(`/api/kills/import-jobs/${jobId}/retry`, { method: 'POST' }),
  deleteKillsImportJob: (jobId: string) =>
    request<void>(`/api/kills/import-jobs/${jobId}`, { method: 'DELETE' }),
  previewKillsImport: (rows: Array<{ name: string; kills: number }>) =>
    request<KillImportPreview>(`/api/kills/import-video/preview`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  confirmKillsImport: async (
    rows: Array<{ playerId: number; kills: number }>,
    jobId?: string | null,
  ) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (jobId) headers['x-kill-import-job-id'] = jobId;
    const res = await fetch('/api/kills/import-video/confirm', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ rows }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body?.error ?? res.statusText);
    }
    return (await res.json()) as { ok: true; applied: number; skipped: number };
  },

  // --- Power video import (Plan 044) ----------------------------------
  uploadPowerImportVideo: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ jobId: string; bytes: number }>(`/api/power/import-video`, {
      method: 'POST',
      body,
    });
  },
  getPowerImportJob: (jobId: string) =>
    request<PowerImportJob>(`/api/power/import-jobs/${jobId}`),
  retryPowerImportJob: (jobId: string) =>
    request<PowerImportJob>(`/api/power/import-jobs/${jobId}/retry`, { method: 'POST' }),
  previewPowerImport: (rows: Array<{ name: string; power: number }>) =>
    request<PowerImportPreview>(`/api/power/import-video/preview`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  confirmPowerImport: async (rows: PowerImportConfirmRow[], jobId?: string | null) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (jobId) headers['x-power-import-job-id'] = jobId;
    const res = await fetch('/api/power/import-video/confirm', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ rows }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body?.error ?? res.statusText);
    }
    return (await res.json()) as { ok: true; applied: number; skipped: number; created: number };
  },

  confirmVsImport: (
    id: number,
    payload: {
      kind: 'weekly' | 'daily';
      dayIndex: number | null;
      rows: Array<{ playerId: number; scoreTenthsM: number }>;
    },
  ) =>
    request<{ ok: true; applied: number; dayIndex?: number }>(
      `/api/vs/${id}/import-video/confirm`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  getScorePolicy: () => request<ScorePolicyDto>('/api/score-policy'),
  updateScorePolicy: (body: Partial<Omit<ScorePolicyDto, 'id' | 'updatedAt' | 'updatedBy' | 'updatedByName'>>) =>
    request<ScorePolicyDto>('/api/score-policy', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  listPlanVariants: (kind?: 'ds' | 'cs') =>
    request<PlanVariantList>(`/api/plan-variants${kind ? `?kind=${kind}` : ''}`),
  createPlanVariant: (body: {
    kind: 'ds' | 'cs';
    name: string;
    basedOn: string;
    config: unknown;
  }) =>
    request<PlanVariantDto>('/api/plan-variants', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updatePlanVariant: (id: number, body: { name?: string; config?: unknown }) =>
    request<PlanVariantDto>(`/api/plan-variants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deletePlanVariant: (id: number) =>
    request<{ ok: true }>(`/api/plan-variants/${id}`, { method: 'DELETE' }),
  setPlanDefault: (kind: 'ds' | 'cs', variantKey: string) =>
    request<{ kind: string; variantKey: string }>(`/api/plan-defaults/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ variantKey }),
    }),

  getChatBans: (token: string) =>
    request<ChatBanState>(
      `/api/self-report/${encodeURIComponent(token)}/chat-ban`,
    ),
  createChatBan: (
    token: string,
    body: { startsAtUtc: string; durationHours: number; notes?: string },
  ) =>
    request<{ ban: ChatBanDto }>(
      `/api/self-report/${encodeURIComponent(token)}/chat-ban`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateChatBan: (
    token: string,
    id: number,
    body: {
      startsAtUtc?: string;
      durationHours?: number;
      notes?: string | null;
    },
  ) =>
    request<{ ban: ChatBanDto }>(
      `/api/self-report/${encodeURIComponent(token)}/chat-ban/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  endChatBan: (token: string, id: number) =>
    request<{ ban: ChatBanDto }>(
      `/api/self-report/${encodeURIComponent(token)}/chat-ban/${id}/end`,
      { method: 'POST' },
    ),
  getAllianceHub: (token: string) =>
    request<AllianceHub>(
      `/api/self-report/${encodeURIComponent(token)}/alliance`,
    ),
  updateSelfReportProfile: (
    token: string,
    body: { timezone?: string | null; profession?: Profession | null },
  ) =>
    request<{
      player: {
        id: number;
        name: string;
        displayName: string | null;
        rank: Rank;
        timezone: string | null;
        profession: Profession | null;
      };
    }>(`/api/self-report/${encodeURIComponent(token)}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  listAdminChatBans: () =>
    request<{ bans: AdminChatBan[] }>(`/api/admin/chat-bans`),
  createAdminChatBan: (body: {
    playerId: number;
    startsAtUtc: string;
    durationHours: number;
    notes?: string;
  }) =>
    request<{ ban: ChatBanDto }>(`/api/admin/chat-bans`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAdminChatBan: (
    id: number,
    body: {
      startsAtUtc?: string;
      durationHours?: number;
      notes?: string | null;
    },
  ) =>
    request<{ ban: ChatBanDto }>(`/api/admin/chat-bans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  endAdminChatBan: (id: number) =>
    request<{ ban: ChatBanDto }>(`/api/admin/chat-bans/${id}/end`, {
      method: 'POST',
    }),
  listAdminPhrases: () =>
    request<{ pools: PhrasePool[]; phrases: PhraseEntry[] }>(
      `/api/admin/phrases`,
    ),
  createAdminPhrase: (poolKey: string, text: string) =>
    request<{ phrase: PhraseEntry }>(
      `/api/admin/phrases/${encodeURIComponent(poolKey)}`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),
  updateAdminPhrase: (
    id: number,
    body: { text?: string; active?: boolean },
  ) =>
    request<{ phrase: PhraseEntry }>(`/api/admin/phrases/entry/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAdminPhrase: (id: number) =>
    request<{ ok: true }>(`/api/admin/phrases/entry/${id}`, {
      method: 'DELETE',
    }),
  getAdminWorstParker: (week?: string) =>
    request<{ pick?: WorstParkerPickDto | null; recent?: WorstParkerPickDto[] }>(
      week
        ? `/api/admin/worst-parker?week=${encodeURIComponent(week)}`
        : `/api/admin/worst-parker`,
    ),
  putAdminWorstParker: (body: {
    weekStart: string;
    playerId: number;
    note?: string | null;
  }) =>
    request<{ pick: WorstParkerPickDto | null }>(`/api/admin/worst-parker`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteAdminWorstParker: (weekStart: string) =>
    request<{ ok: true }>(
      `/api/admin/worst-parker/${encodeURIComponent(weekStart)}`,
      { method: 'DELETE' },
    ),
  getAdminHallOfFame: () => request<AllianceHub>(`/api/admin/hall-of-fame`),
  getSelfReportKillGrowth: (token: string, range: KillGrowthRange) =>
    request<PlayerKillGrowth>(
      `/api/self-report/${encodeURIComponent(token)}/kill-growth?range=${range}`,
    ),
  getAdminKillGrowth: (range: KillGrowthRange) =>
    request<KillGrowth>(`/api/admin/kill-growth?range=${range}`),
  // Insights (Plans 050–055)
  getAdminReporting: () => request<Reporting>('/api/admin/insights/reporting'),
  getSelfReportReporting: (token: string) =>
    request<PlayerReporting>(`/api/self-report/${encodeURIComponent(token)}/reporting`),
  getAdminStrengthGrowth: (month?: string) =>
    request<StrengthGrowth>(
      `/api/admin/insights/strength-growth${month ? `?month=${month}` : ''}`,
    ),
  getAdminTroopMix: () => request<TroopMix>('/api/admin/insights/troop-mix'),
  getAdminCombatEfficiency: (range: CombatRange) =>
    request<CombatEfficiency>(`/api/admin/insights/combat-efficiency?range=${range}`),
  getSelfReportCombatEfficiency: (token: string, range: CombatRange) =>
    request<PlayerCombatEfficiency>(
      `/api/self-report/${encodeURIComponent(token)}/combat-efficiency?range=${range}`,
    ),
  getAdminWeeklyReport: (week: string | null) =>
    request<WeeklyReport>(`/api/admin/insights/weekly${week ? `?week=${week}` : ''}`),
  getAdminAllianceTrend: (months: TrendMonths) =>
    request<AllianceTrend>(`/api/admin/insights/alliance-trend?months=${months}`),
  getAdminVsPower: (weeks: VsPowerWeeks) =>
    request<VsPower>(`/api/admin/insights/vs-power?weeks=${weeks}`),
  getSelfReportVsPower: (token: string, weeks: VsPowerWeeks) =>
    request<PlayerVsPower>(
      `/api/self-report/${encodeURIComponent(token)}/vs-power?weeks=${weeks}`,
    ),
  getSelfReportStrengthGrowth: (token: string, month?: string) =>
    request<PlayerStrengthGrowth>(
      `/api/self-report/${encodeURIComponent(token)}/strength-growth${month ? `?month=${month}` : ''}`,
    ),
};

export type PhraseCategory =
  | 'chat_ban'
  | 'strength'
  | 'vs_slacker'
  | 'chase'
  | 'worst_parker';

export interface PhrasePool {
  poolKey: string;
  label: string;
  description: string | null;
  category: PhraseCategory;
  sortOrder: number;
}

export interface PhraseEntry {
  id: number;
  poolKey: string;
  text: string;
  active: boolean;
}

export interface WorstParkerPickDto {
  weekStart: string;
  note: string | null;
  createdAt: string;
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
  };
}

export interface AdminChatBan extends ChatBanDto {
  player: {
    id: number;
    name: string;
    displayName: string | null;
    rank: Rank;
  };
}

export interface ChatBanDto {
  id: number;
  startsAtUtc: string;
  durationHours: number;
  endedAt: string | null;
  endsAtUtc: string;
  active: boolean;
  notes: string | null;
  createdAt: string;
  effectiveHours: number;
}

export interface ChatBanState {
  active: ChatBanDto | null;
  history: ChatBanDto[];
  constraints: {
    minHours: number;
    maxHours: number;
    stepHours: number;
  };
}

export interface AllianceHubPlayer {
  id: number;
  name: string;
  displayName: string | null;
  rank: Rank;
}

export interface AllianceHubTrainLeader {
  date: string;
  notes: string | null;
  player: AllianceHubPlayer;
}

export interface AllianceHubChatBanEntry {
  player: AllianceHubPlayer;
  ban: ChatBanDto;
  quip: string;
}

export interface AllianceHubHighscore {
  count: { player: AllianceHubPlayer; count: number }[];
  totalHours: { player: AllianceHubPlayer; totalHours: number }[];
}

export interface AllianceHubVsSlackers {
  date: string;
  dayIndex: number;
  players: {
    player: AllianceHubPlayer;
    scoreTenthsM: number;
    quip: string;
  }[];
}

export interface AllianceHubVsKindRoast {
  kind: 'push' | 'save';
  weekStart: string;
  weekEnd: string;
  player: AllianceHubPlayer;
  totalM: number;
  quip: string;
}

export interface AllianceHubVsWeeklySlackers {
  weekStart: string;
  weekEnd: string;
  weeklyTargetM: number;
  daysPlayed: number;
  players: {
    player: AllianceHubPlayer;
    totalM: number;
    daysBelow: number;
    quip: string;
  }[];
  daysBelowTop: {
    player: AllianceHubPlayer;
    daysBelow: number;
    totalM: number;
  }[];
}

export interface AllianceHubStrengthEntry {
  player: AllianceHubPlayer;
  deltaM: number;
  fromM: number;
  toM: number;
  /** Set when a boundary value is interpolated across a gap: [before, after]. */
  fromEstimatedFrom: [string, string] | null;
  toEstimatedFrom: [string, string] | null;
  quip: string;
}

/** Strength push of the previous calendar month (Plan 048). */
export interface AllianceHubStrengthTop {
  periodKey: string;
  periodLabel: string;
  windowStartIso: string;
  windowEndIso: string;
  /** A report within this many days of each boundary is required. */
  maxGapDays: number;
  /** Active players with M values reported during the month. */
  reported: number;
  /** …of those, the ones measurable over the whole month. */
  ranked: number;
  notComparable: number;
  m1: AllianceHubStrengthEntry | null;
  mSum: AllianceHubStrengthEntry | null;
  pct: (AllianceHubStrengthEntry & { pct: number }) | null;
}

export interface AllianceHubBestTrainStart {
  serverStart: string;
  serverEnd: string;
  count: number;
  considered: number;
  boardingHours: number;
  alternatives: string[];
}

export interface AllianceHubTrainTile {
  date: string;
  kind: TrainKind;
  setAt: string;
  departsAt: string;
  score: number | null;
  player: AllianceHubPlayer;
  vip: { kind: TrainVipKind | null; player: AllianceHubPlayer } | null;
  quip: string;
}

export interface AllianceHubMegaExpressWindow {
  weekStart: string; // range start (inclusive)
  weekEnd: string; // range end (inclusive)
  total: number;
  top: { player: AllianceHubPlayer; count: number }[];
}

export interface AllianceHubLastMegaExpress {
  date: string;
  score: number | null;
  player: AllianceHubPlayer;
  vip: { kind: string | null; player: AllianceHubPlayer } | null;
  quip: string;
}

export interface AllianceHub {
  weekStart: string;
  today: string;
  trainLeaders: AllianceHubTrainLeader[];
  trainWeekDescription: string | null;
  bestTrainStart: AllianceHubBestTrainStart | null;
  trainCountdown: AllianceHubTrainTile | null;
  megaExpressesToday: AllianceHubTrainTile[];
  megaExpressLast7d: AllianceHubMegaExpressWindow | null;
  megaExpressCalendarWeek: AllianceHubMegaExpressWindow | null;
  lastMegaExpress: AllianceHubLastMegaExpress | null;
  chatBans: {
    active: AllianceHubChatBanEntry[];
    highscores: AllianceHubHighscore;
  };
  vsUnderperformers: AllianceHubVsSlackers | null;
  vsWeeklySlackers: AllianceHubVsWeeklySlackers | null;
  vsKindRoast: AllianceHubVsKindRoast | null;
  strengthTop: AllianceHubStrengthTop | null;
  aboveWeight: AllianceHubAboveWeight | null;
  worstParker: AllianceHubWorstParker | null;
  ironThumbs: AllianceHubIronThumbs | null;
  sleepingBeauty: AllianceHubSleepingBeauty | null;
  glowUp: AllianceHubGlowUp | null;
  busterWeek: AllianceHubBusterWeek | null;
}

export interface AllianceHubWorstParker {
  weekStart: string;
  note: string | null;
  player: AllianceHubPlayer;
  quip: string;
}

export interface AllianceHubIronThumbsEntry {
  player: AllianceHubPlayer;
  count: number;
  deltaM: number;
  avgM: number;
  quip: string;
}

export interface AllianceHubIronThumbs {
  top: AllianceHubIronThumbsEntry[];
}

export interface AllianceHubSleepingBeautyEntry {
  player: AllianceHubPlayer;
  days: number;
  quip: string;
}

export interface AllianceHubSleepingBeauty {
  top: AllianceHubSleepingBeautyEntry[];
}

export interface AllianceHubGlowUp {
  player: AllianceHubPlayer;
  pct: number;
  fromM: number;
  toM: number;
  days: number;
  quip: string;
}

export interface AllianceHubBusterWeek {
  player: AllianceHubPlayer;
  deltaKills: number;
  saturdayIso: string;
  referenceRecordedAt: string;
  latestRecordedAt: string;
  quip: string;
}

export interface PlanVariantDto {
  id: number;
  kind: 'ds' | 'cs';
  key: string;
  name: string;
  basedOn: string | null;
  config: unknown;
  createdBy: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface PlanDefaultDto {
  kind: 'ds' | 'cs';
  variantKey: string;
}

export interface PlanVariantList {
  variants: PlanVariantDto[];
  defaults: PlanDefaultDto[];
}

export interface Alliance {
  id: number;
  name: string;
  tag: string | null;
  notes: string | null;
  active: boolean;
  /** Tenant alliances (own or other tool users on the server) are read-only. */
  isTenant: boolean;
  own: boolean;
  createdAt: string;
}

export const TRANSFER_SEAT_COLORS = ['gold', 'purple', 'blue', 'white'] as const;
export type TransferSeatColor = (typeof TRANSFER_SEAT_COLORS)[number];

export interface TransferWindowRow {
  id: number;
  name: string;
  notes: string | null;
  seatsGold: number | null;
  seatsPurple: number | null;
  seatsBlue: number | null;
  seatsWhite: number | null;
  closedAt: string | null;
  closedBy: number | null;
  createdAt: string;
  createdBy: number | null;
  counts: { total: number; confirmed: number; accepted: number };
}

export interface TransferWindowOpen {
  id: number;
  name: string;
}

export const TRANSFER_PLAYER_KINDS = ['incoming', 'leaver'] as const;
export type TransferPlayerKind = (typeof TRANSFER_PLAYER_KINDS)[number];

export type TransferSeatBucket = TransferSeatColor | 'other';

export interface TransferPlayer {
  id: number;
  transferWindowId: number;
  name: string;
  sourceServer: string;
  originAlliance: string | null;
  thp: number | null;
  confirmed: boolean;
  accepted: boolean;
  seatColor: TransferSeatColor | null;
  notes: string | null;
  kind: TransferPlayerKind;
  targetAllianceId: number | null;
  playerId: number | null;
  createdAt: string;
  createdBy: number | null;
  tags: string[];
}

export interface TransferStats {
  current: { count: number; thpSum: number; thpAvg: number };
  leavers: { count: number; confirmed: number; thpSum: number; thpAvg: number };
  incoming: {
    count: number;
    confirmed: number;
    accepted: number;
    thpSum: number;
    thpAvg: number;
    thpAcceptedSum: number;
    thpAcceptedAvg: number;
  };
  projected: { count: number; thpSum: number; thpAvg: number };
}

export interface TransferAllianceOverviewRow {
  allianceId: number;
  name: string;
  tag: string | null;
  count: number;
  confirmed: number;
  accepted: number;
  seats: Record<TransferSeatBucket, number>;
  thpSum: number;
  thpAvg: number;
}

export interface TransferTagStat {
  tag: string;
  count: number;
  confirmed: number;
  accepted: number;
  seats: Record<TransferSeatBucket, number>;
  thpSum: number;
}

export interface TransferWindowDetail {
  window: Omit<TransferWindowRow, 'counts'>;
  players: TransferPlayer[];
  leavers: TransferPlayer[];
  tags: string[];
  counts: { total: number; confirmed: number; accepted: number };
  stats: TransferStats;
  serverOverview: TransferAllianceOverviewRow[];
  tagStats: TransferTagStat[];
  seatBuckets: TransferSeatBucket[];
}

export interface KickWeights {
  id: number;
  noShowWeight: number;
  lateWeight: number;
  attendanceLookbackDays: number;
  saveOvershootWeight: number;
  saveOvershootCapTenths: number;
  pushUndershootWeight: number;
  pushTargetTenths: number;
  vsLookbackWeeks: number;
  lowStrengthWeight: number;
  highStrengthBonus: number;
  strengthLowPct: number;
  strengthHighPct: number;
  r1Weight: number;
  r2Weight: number;
  r3Weight: number;
  r4Weight: number;
  r5Weight: number;
  openEndedLeaveWeight: number;
  longLeaveDaysThreshold: number;
  longLeaveWeight: number;
  yellowCardWeight: number;
  banWeight: number;
  updatedAt: string;
  updatedBy: number | null;
}

export interface KickBreakdownItem {
  key: string;
  label: string;
  points: number;
  detail?: string;
}

export interface KickCandidate {
  playerId: number;
  name: string;
  rank: Rank;
  thp: number | null;
  score: number;
  breakdown: KickBreakdownItem[];
  alreadyLeaver: boolean;
}

export interface TransferPlayerInput {
  name: string;
  sourceServer?: string;
  originAlliance?: string | null;
  thp?: number | null;
  notes?: string | null;
  tags?: string[];
  confirmed?: boolean;
  accepted?: boolean;
  seatColor?: TransferSeatColor | null;
  kind?: TransferPlayerKind;
  targetAllianceId?: number | null;
  playerId?: number | null;
}

export interface ScorePolicyDto {
  id: number;
  notPickedBonusPerStreak: number;
  notPickedMaxBonus: number;
  noShowPenaltyPerStreak: number;
  noShowMaxPenalty: number;
  latePenaltyPerStreak: number;
  lateMaxPenalty: number;
  subsOnlyBonusPerStreak: number;
  subsOnlyMaxBonus: number;
  notRegisteredBonusPerStreak: number;
  notRegisteredMaxBonus: number;
  yellowCardFactor: number;
  greenFlagFactor: number;
  csSlotPreferenceFactor: number;
  updatedAt: string;
  updatedBy: number | null;
  updatedByName?: string | null;
}

export type VsKind = 'save' | 'soft_push' | 'push' | 'aborted_push';
export type VsOutcome = 'win' | 'loss';

export interface VsWeek {
  id: number;
  weekStart: string;
  kind: VsKind;
  opponentName: string;
  opponentServer: string;
  notes: string | null;
  outcome: VsOutcome | null;
  lockedAt: string | null;
  lockedBy: number | null;
  createdAt: string;
  createdBy: number | null;
}

export interface VsTopScorer {
  playerId: number;
  name: string;
  displayName: string | null;
  scoreM: number;
}

export interface VsWeekRow extends VsWeek {
  playerCount: number;
  totalScoreM: number;
  topScorer: VsTopScorer | null;
}

export interface VsTopPlayerEntry {
  dayIndex: number;
  playerId: number;
  pinned?: boolean;
}

export interface VsDailyScore {
  playerId: number;
  dayIndex: number;
  scoreTenthsM: number;
  updatedAt?: string;
}

export interface VsImportVideoJob {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'needs_kind_pick' | 'discarded';
  stage: 'queued' | 'uploading' | 'processing' | 'done' | string;
  kind: 'weekly' | 'daily';
  requestedDayIndex: number | null;
  videoBytes?: number;
  attempts?: number;
  nextRetryAt?: string | null;
  createdAt?: string;
  createdBy?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  appliedAt?: string | null;
  videoAvailable?: boolean;
  error: string | null;
  extraction: {
    rows: Array<{ name: string; scoreTenthsM: number }>;
    detected_view: 'weekly' | 'daily' | 'unknown' | null;
    detected_day_index: number | null;
  } | null;
}

export interface VsImportMatchedRow {
  ocrName: string;
  scoreTenthsM: number;
  player: { id: number; name: string; displayName: string | null };
  matchKind: 'exact_name' | 'exact_display' | 'fuzzy_unique';
  matchScore: number;
}

export interface VsImportUnmatchedRow {
  ocrName: string;
  scoreTenthsM: number;
  suggestions: Array<{
    playerId: number;
    name: string;
    displayName: string | null;
    score: number;
  }>;
}

export interface VsImportPreview {
  kind: 'weekly' | 'daily';
  dayIndex: number | null;
  matched: VsImportMatchedRow[];
  unmatched: VsImportUnmatchedRow[];
}

export interface KillImportJob {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'discarded';
  stage: 'queued' | 'uploading' | 'processing' | 'done' | string;
  videoBytes?: number;
  attempts?: number;
  nextRetryAt?: string | null;
  createdAt?: string;
  createdBy?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  appliedAt?: string | null;
  videoAvailable?: boolean;
  error: string | null;
  extraction: { rows: Array<{ name: string; kills: number }> } | null;
}

export interface KillImportMatchedRow {
  ocrName: string;
  kills: number;
  killsM: number;
  baselineKillsM: number;
  regression: boolean;
  unchanged: boolean;
  player: { id: number; name: string; displayName: string | null };
  matchKind: 'exact_name' | 'exact_display' | 'fuzzy_unique';
  matchScore: number;
}

export interface KillImportUnmatchedRow {
  ocrName: string;
  kills: number;
  killsM: number;
  suggestions: Array<{
    playerId: number;
    name: string;
    displayName: string | null;
    score: number;
  }>;
}

export interface KillImportPreview {
  matched: KillImportMatchedRow[];
  unmatched: KillImportUnmatchedRow[];
}

export type PowerImportJob = Omit<KillImportJob, 'extraction'> & {
  extraction: { rows: Array<{ name: string; power: number }> } | null;
};

export interface PowerImportMatchedRow {
  ocrName: string;
  power: number;
  powerM: number;
  baselinePowerM: number | null;
  unchanged: boolean;
  decrease: boolean;
  player: { id: number; name: string; displayName: string | null; active: boolean };
  matchKind: 'exact_name' | 'exact_display' | 'fuzzy_unique';
  matchScore: number;
}

export interface PowerImportUnmatchedRow {
  ocrName: string;
  power: number;
  powerM: number;
  suggestions: KillImportUnmatchedRow['suggestions'];
}

export interface PowerImportPreview {
  matched: PowerImportMatchedRow[];
  unmatched: PowerImportUnmatchedRow[];
}

export type PowerImportConfirmRow =
  | { playerId: number; power: number }
  | { create: { name: string; rank: Rank }; power: number };

export interface VsInput {
  weekStart: string;
  kind: VsKind;
  opponentName: string;
  opponentServer: string;
  notes?: string | null;
}

export interface VsOpponentSuggestion {
  name: string;
  server: string;
  lastSeen: string;
}

export interface VsPlayerScore {
  playerId: number;
  scoreM: number;
  updatedAt: string;
}

export interface VsDetailPlayer {
  id: number;
  name: string;
  displayName: string | null;
  rank: Rank;
  active: boolean;
}

export interface VsDetail {
  vs: VsWeek;
  scores: VsPlayerScore[];
  players: VsDetailPlayer[];
  topPlayers: VsTopPlayerEntry[];
  dailyScores: VsDailyScore[];
}

export interface VsOverviewWeek {
  id: number;
  weekStart: string;
  kind: VsKind;
  opponentName: string;
  opponentServer: string;
  outcome: VsOutcome | null;
  twentiethM: number | null;
  topScorerPlayerId: number | null;
  /** Per-day top-N cut in tenths of M, length 6, or null when no daily data. */
  dayCutsTenthsM: (number | null)[] | null;
}

export interface VsPolicy {
  topRank: number;
  orangeFactor: number;
  redFactor: number;
  redAbsoluteM: number;
}

export interface VsOverviewPlayer {
  playerId: number;
  name: string;
  displayName: string | null;
  rank: Rank;
  active: boolean;
  scores: Record<number, number>;
  totalM: number;
  pushTotalM: number;
  flagLowTotal: boolean;
  dayWinsByWeek: Record<number, number[]>;
  /** Own daily score in tenths of M per weekId → array length 6. */
  dailyByWeek: Record<number, (number | null)[]>;
}

export interface VsOverview {
  weeks: VsOverviewWeek[];
  players: VsOverviewPlayer[];
  lowTotalThresholdM: number;
  policy: VsPolicy;
}

export interface VsCurrent {
  weekStart: string;
  kind: VsKind;
  opponentName: string;
  opponentServer: string;
  outcome: VsOutcome | null;
}

export const RANK_LABEL: Record<Rank, string> = {
  R5: 'R5 (Boss)',
  R4: 'R4 (Leadership)',
  R3: 'R3',
  R2: 'R2',
  R1: 'R1',
};
const RANK_VALUE: Record<Rank, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };
export const canEdit = (role: Rank) => RANK_VALUE[role] >= 4;
export const canManageUsers = (role: Rank) => RANK_VALUE[role] >= 5;

export type KillGrowthRange = '30d' | '90d' | '180d';
export type KillGrowthClass = 'top' | 'mid' | 'rising';

export interface KillGrowthPlayerRow {
  player: { id: number; name: string; displayName: string | null; rank: Rank };
  class: KillGrowthClass;
  mSum: number;
  startKills: number;
  endKills: number;
  deltaM: number;
  pct: number;
  baselineAt: string;
  /** Start value interpolated across a reporting gap: [before, after]. */
  baselineEstimatedFrom: [string, string] | null;
  latestAt: string;
  /** Kills (M) at each of `samples`. */
  spark: number[];
}

export interface KillGrowth {
  range: KillGrowthRange;
  startIso: string;
  endIso: string;
  samples: string[];
  classes: {
    key: KillGrowthClass;
    /** M-Sum span of the class (lower / upper limit). */
    minMSum: number | null;
    maxMSum: number | null;
    players: number;
    /** Players with snapshots at both ends but no change in kills. */
    noNewKills: number;
    medianPct: number | null;
    medianDeltaM: number | null;
    totalDeltaM: number;
    /** Median kills indexed to 100 at the baseline, per sample. */
    indexed: (number | null)[];
  }[];
  allianceIndexed: (number | null)[];
  players: KillGrowthPlayerRow[];
  missingBaseline: number;
  /** Snapshots ignored as typos (above a later value or implausibly large). */
  suspicious: {
    player: { id: number; name: string; displayName: string | null; rank: Rank };
    kills: number;
    recordedAt: string;
  }[];
}

export interface PlayerKillGrowth {
  range: KillGrowthRange;
  startIso: string;
  endIso: string;
  samples: string[];
  own: {
    class: KillGrowthClass;
    startKills: number;
    endKills: number;
    deltaM: number;
    pct: number;
    baselineAt: string;
    baselineEstimatedFrom: [string, string] | null;
    /** Own kills indexed to 100 at the baseline, per sample. */
    indexed: number[];
    classPosition: number;
    classSize: number;
    /** Share of classmates with lower growth, 0–100. */
    aheadOfPct: number | null;
  } | null;
  classMedian: {
    medianPct: number | null;
    medianDeltaM: number | null;
    minMSum: number | null;
    maxMSum: number | null;
    indexed: (number | null)[];
  } | null;
  allianceMedianPct: number | null;
}

// ---- Insights (Plans 050–055) ----

export interface InsightsPlayer {
  id: number;
  name: string;
  displayName: string | null;
  rank: string;
}

export type ReportingStatus = 'ok' | 'at_risk' | 'missing';

export interface ReportingRow {
  player: InsightsPlayer;
  lastReportAt: string | null;
  daysSinceReport: number | null;
  medianGapDays: number | null;
  reports30d: number;
  status: ReportingStatus;
  /** Report before this moment to stay comparable. */
  reportByIso: string | null;
}

export interface Reporting {
  nowIso: string;
  monthStartIso: string;
  nextBoundaryIso: string;
  maxGapDays: number;
  players: ReportingRow[];
  alliance: {
    activePlayers: number;
    share14d: number | null;
    medianGapDays: number | null;
    counts: { ok: number; atRisk: number; missing: number };
    monthly: { month: string; label: string; comparableShare: number | null }[];
  };
}

export interface PlayerReporting {
  maxGapDays: number;
  nextBoundaryIso: string;
  own: {
    status: ReportingStatus;
    reportByIso: string | null;
    lastReportAt: string | null;
  } | null;
}

export interface InsightsMonth {
  key: string;
  label: string;
}

export interface StrengthGrowthRow {
  player: InsightsPlayer;
  class: KillGrowthClass;
  fromMSum: number;
  toMSum: number;
  deltaM: number;
  pct: number;
  fromEstimatedFrom: [string, string] | null;
  toEstimatedFrom: [string, string] | null;
  /** Months in a row (up to this one) above the class median. */
  streak: number;
}

export interface StrengthGrowthClassSummary {
  key: KillGrowthClass;
  players: number;
  minMSum: number | null;
  maxMSum: number | null;
  medianDeltaM: number | null;
  medianPct: number | null;
  best: { player: InsightsPlayer; pct: number; deltaM: number } | null;
}

export interface StrengthGrowth {
  month: string;
  monthLabel: string;
  windowStartIso: string;
  windowEndIso: string;
  months: InsightsMonth[];
  classes: StrengthGrowthClassSummary[];
  players: StrengthGrowthRow[];
  notComparable: number;
  allianceMedianPct: number | null;
  allianceMedianDeltaM: number | null;
}

export interface PlayerStrengthGrowth {
  month: string;
  monthLabel: string;
  months: InsightsMonth[];
  own: {
    class: KillGrowthClass;
    fromMSum: number;
    toMSum: number;
    deltaM: number;
    pct: number;
    fromEstimatedFrom: [string, string] | null;
    toEstimatedFrom: [string, string] | null;
    streak: number;
    classPosition: number;
    classSize: number;
    aheadOfPct: number | null;
  } | null;
  classMedian: {
    medianPct: number | null;
    medianDeltaM: number | null;
    minMSum: number | null;
    maxMSum: number | null;
  } | null;
  allianceMedianPct: number | null;
}

export interface TroopMix {
  playersWithData: number;
  activePlayers: number;
  byType: { type: MType; m1Players: number; m1Power: number; squadPower: number; squads: number }[];
  byClass: { key: KillGrowthClass; players: number; m1: Record<MType, number> }[];
  switches: {
    player: InsightsPlayer;
    at: string;
    from: MType;
    to: MType;
    m1Before: number;
    m1After: number;
  }[];
  monthly: { month: string; label: string; players: number; m1: Record<MType, number> }[];
}

export type VsPowerWeeks = 4 | 12;

export interface VsPowerRow {
  player: InsightsPlayer;
  class: KillGrowthClass;
  weeks: number;
  /** Median weekly VS points per M of M-Sum. */
  ratio: number;
  vsMedian: number;
  mSum: number;
  /** 100 = class median ratio. */
  index: number | null;
  classRank: number;
  classSize: number;
}

export interface VsPower {
  weeks: number;
  weekStarts: string[];
  minWeeks: number;
  classes: {
    key: KillGrowthClass;
    players: number;
    minMSum: number | null;
    maxMSum: number | null;
    medianRatio: number | null;
    medianVs: number | null;
  }[];
  players: VsPowerRow[];
  notComparable: number;
}

export interface PlayerVsPower {
  weeks: number;
  minWeeks: number;
  own: Omit<VsPowerRow, 'player'> | null;
  classMedian: {
    medianRatio: number | null;
    medianVs: number | null;
    minMSum: number | null;
    maxMSum: number | null;
  } | null;
}

/** Hall of Fame: best VS per M vs. own class, last 4 weeks (Plan 052). */
export interface AllianceHubAboveWeight {
  player: AllianceHubPlayer;
  weeks: number;
  index: number;
  ratio: number;
  vsMedian: number;
  mSum: number;
  class: KillGrowthClass;
  quip: string;
}

export type CombatRange = '30d' | '90d';
export type CombatProfile = 'fighting' | 'growing' | null;

export interface CombatEfficiencyRow {
  player: InsightsPlayer;
  class: KillGrowthClass;
  deltaKillsM: number;
  killsEstimated: boolean;
  /** M-Sum at the range's midpoint. */
  mSum: number;
  /** Kills gained (M) per M of M-Sum. */
  efficiency: number;
  growthPct: number | null;
  /** 100 = class median efficiency. */
  index: number | null;
  profile: CombatProfile;
  classRank: number;
  classSize: number;
}

export interface CombatEfficiency {
  range: CombatRange;
  startIso: string;
  endIso: string;
  classes: {
    key: KillGrowthClass;
    players: number;
    medianEfficiency: number | null;
    medianDeltaKillsM: number | null;
    medianGrowthPct: number | null;
  }[];
  players: CombatEfficiencyRow[];
  notComparable: number;
}

export interface PlayerCombatEfficiency {
  range: CombatRange;
  own: Pick<
    CombatEfficiencyRow,
    | 'class'
    | 'deltaKillsM'
    | 'killsEstimated'
    | 'mSum'
    | 'efficiency'
    | 'index'
    | 'profile'
    | 'classRank'
    | 'classSize'
  > | null;
  classMedian: { medianEfficiency: number | null; medianDeltaKillsM: number | null } | null;
}

export interface WeeklyStorm {
  date: string;
  status: 'planned' | 'completed' | 'skipped';
  registered: number;
  /** Registered but neither starter nor sub. */
  notPicked: number;
  picked: number;
  starters: number;
  subs: number;
  showed: number;
  late: number;
  noShow: number;
  pending: number;
  attendanceRate: number | null;
}

type WeeklyPlayer = { id: number; name: string; displayName: string | null };

export interface WeeklyReport {
  weekStart: string;
  weekEnd: string;
  isComplete: boolean;
  prevWeek: string;
  nextWeek: string | null;
  vs: {
    kind: VsKind;
    opponentName: string;
    opponentServer: string;
    outcome: VsOutcome | null;
    totalM: number;
    players: number;
    atTarget: number;
    targetM: number;
    dayTotalsM: (number | null)[];
    /** highscore: push / soft push; save: closest to 7.2 M every day Mon–Thu. */
    topMode: 'highscore' | 'save';
    /** `overM`: save weeks only — total M above 7.2 M over Mon–Thu. */
    top: { player: WeeklyPlayer; scoreM: number; overM: number | null }[];
  } | null;
  cs: WeeklyStorm | null;
  ds: WeeklyStorm | null;
  /** CS + DS combined: unique registered players, and those picked for neither. */
  storms: { uniqueRegistered: number; notPickedAtAll: number };
  trains: { regular: number; megaExpress: number };
  kills: {
    measured: number;
    totalM: number;
    medianM: number | null;
    top: { player: WeeklyPlayer; deltaKillsM: number }[];
    lastImportAt: string | null;
    importNearEnd: boolean;
  };
  reporting: { reported: number; members: number; atRisk: number; missing: number };
  members: { count: number; joined: number; left: number };
  /** ≥ 7.2 M every counted VS day, showed at picked CS/DS, power reported. */
  perfect: { count: number; vsDays: number; players: WeeklyPlayer[] };
  previous: {
    vsKind: VsKind | null;
    vsTotalM: number | null;
    killsMedianM: number | null;
    csRate: number | null;
    dsRate: number | null;
    reported: number;
    perfect: number;
  };
}

export type TrendMonths = 6 | 12;

export interface AllianceTrendMonth {
  month: string;
  label: string;
  members: number;
  strength: { measured: number; medianMSum: number | null; topSumMSum: number | null };
  kills: { measured: number; medianKillsM: number | null; topSumKillsM: number | null };
  growth: { measured: number; medianPct: number | null };
  vs: {
    weeks: number;
    won: number;
    lost: number;
    avgWeeklyTotalM: number | null;
    medianPlayerWeekM: number | null;
  };
}

export interface AllianceTrend {
  topN: number;
  months: AllianceTrendMonth[];
}
